import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
// Menu and ArrowLeft no longer used here — mobile menu moved to Sidebar.tsx
// import { Menu, ArrowLeft } from 'lucide-react';
import { Wrench } from 'lucide-react';
// Sin `useNavigate`: el único que lo usaba acá era el guard de marca en borrador, que
// se mudó a `useShellGuards` para que el shell nuevo corra exactamente los mismos
// guards (ADR-2026-07-31-shell-nuevo-urls-viejas).
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { Sidebar } from './components/Sidebar';
import { MobileBottomNav } from './components/MobileBottomNav';
import { MOBILE_RUTAS_SIN_NAV } from './components/mobileNavConfig';
import { esRutaShellLegacy } from './lib/rutasShellLegacy';
// Barra inferior nueva + sheet de Generar del shell mobile
// (ADR-2026-08-04-la-barra-inferior-vive-en-el-shell).
import { NavInferiorMobile } from './pages/v2/components/NavInferiorMobile';
import { SheetGenerar } from './pages/v2/components/SheetGenerar';
import { ModeExamplesModal } from './pages/v2/components/ModeExamplesModal';
import { useModosGenerar } from './pages/v2/useModosGenerar';
import { useBrandProfile } from './hooks/useBrandProfile';
import { rubroMarcaCacheado } from './hooks/useRubroMarca';
import { useNonceDeMarca } from './hooks/useNonceDeMarca';
import { homeAbreConElAgente, rubroVePlantillas, rubroVePlantillasBelleza } from './lib/rubro';
import { useUserGroup } from './hooks/useUserGroup';
import { useMaintenanceFlag } from './hooks/useMaintenanceFlag';
import { landingIsV2 } from './lib/userGroups';
import { identifyUser, resetPostHog } from './lib/posthog';
import { usePresencia } from './hooks/usePresencia';
import { useSincronizarPush } from './hooks/useSincronizarPush';
import { SidebarProvider, useSidebar } from './context/SidebarContext';
import { PermissionProvider } from './context/PermissionContext';
// Chat con el agente de Atlas: el estado vive arriba del árbol para que el
// panel lateral y la pantalla dedicada compartan la misma conversación.
// `useEmpujeChat` da el ancho con el que el chat empuja el contenido — el
// MISMO que usa el panel para dibujarse.
import {
  BotonFlotanteChat,
  ChatAtlasRaiz,
  PanelChatAtlas,
  PantallaChatAtlas,
  RUTA_CHAT_ATLAS,
  useEmpujeChat,
} from './chat-atlas';
import { MobileBlocker } from './components/MobileBlocker';
import { ScrollToTop } from './components/ScrollToTop';
import { GuardiaDelCuestionario } from './components/GuardiaDelCuestionario';
import { PlaceholderPage } from './pages/PlaceholderPage';
import Welcome from './pages/Welcome';
import { Auth } from './pages/Auth';
import GrabarVoz from './pages/GrabarVoz';
import TestMasivo from './pages/test-masivo/TestMasivo';
import GenerarAudio from './pages/GenerarAudio';
import GrabarDialogo from './pages/GrabarDialogo';
import { Settings } from './pages/Settings';
import Lanzamientos from './pages/lanzamientos/Lanzamientos';
import LanzamientoDetalle from './pages/lanzamientos/LanzamientoDetalle';
import CalendarioDeInstagram from './pages/lanzamientos/redes/CalendarioDeInstagram';
import MetricasDePlataformaPage from './pages/lanzamientos/metricas/MetricasDePlataforma';
import { SalesRegister } from './pages/SalesRegister';
import { DataUpload } from './pages/DataUpload';
import { AutomaticSync } from './pages/AutomaticSync';
import { PriceList } from './pages/PriceList';
import { Discounts } from './pages/Discounts';
import { Stock } from './pages/Stock';
import { Messages } from './pages/Messages';
import { InteractionCharts } from './pages/InteractionCharts';
import { Billing } from './pages/Billing';
import { supabase } from './lib/supabase';
import { cacheOwner, debeApagarLoadingTrasMismatch, setCacheOwner } from './utils/sessionCache';
import { Session, User } from '@supabase/supabase-js';
import { retrySupabase, onCircuitChange, getCircuitState, resetCircuit } from './utils/retry';
// WelcomeModal component is temporarily disabled but kept in codebase
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { UpdateInfoModal } from './components/UpdateInfoModal';
import { AnuncioModal } from './components/AnuncioModal';
import { GeneratedImagesHistory } from './pages/GeneratedImagesHistory';
import { ExportarTiendanube } from './pages/ExportarTiendanube';
import { Registro } from './pages/Registro';
import TiendanubeAuth from './pages/tiendanube/TiendanubeAuth';
import TiendanubeConectar from './pages/tiendanube/TiendanubeConectar';
import TiendanubeContinuar from './pages/tiendanube/TiendanubeContinuar';
import TiendanubeProducto from './pages/tiendanube/TiendanubeProducto';
// La SECCIÓN DEDICADA (estudio con sidebar: Inicio/Productos/Creaciones/Exportar + generar/catálogo)
// queda ESCONDIDA/GUARDADA para el futuro plugin integrado de Tiendanube (decisión 2026-08-20). Los
// componentes siguen en `pages/tiendanube/` (Shell, Inicio, Productos, Creaciones, Exportar,
// GenerarModal) pero sin ruta montada — se reactivan cuando se retome el plugin.
import { AccountSetupLayout } from './components/onboarding/AccountSetupLayout';
import { ONBOARDING_V2 } from './components/onboarding/onboardingV2Routes';
import { onboardingPendiente, rubroVisible } from './lib/onboardingPendiente';
import {
  cuestionarioCompletado,
  marcarCuestionarioPendiente,
  rutaExentaDelCuestionario,
} from './lib/cuestionarioObligatorio';
import { useIsDesktop } from './components/onboarding/useIsDesktop';
// El ancho desde el que va la sidebar en flujo (768px), que NO es el mismo que
// "pantalla grande" — ADR-2026-08-05-el-shell-nuevo-arranca-en-768.
import { useShellEscritorio } from './hooks/useShellEscritorio';
import { ReanudadorDeGeneraciones } from './pages/v2/useGeneracionesEnCurso';
import { AvisoGenerandoDeFondo } from './components/AvisoGenerandoDeFondo';
import { AccountSetup } from './pages/AccountSetup';
import { AccountSetupCategory } from './pages/AccountSetupCategory';
import { AccountSetupContent } from './pages/AccountSetupContent';
import { AccountSetupProduct } from './pages/AccountSetupProduct';
import { AccountSetupGenerating } from './pages/AccountSetupGenerating';
import { AccountSetupResults } from './pages/AccountSetupResults';
import { OnboardingMarcaLayout } from './components/onboarding/marca/OnboardingMarcaLayout';
import { LinkDeMarca } from './pages/onboardingMarca/LinkDeMarca';
import { TableroDeMarca } from './pages/onboardingMarca/TableroDeMarca';
import { PreguntasDeMarca } from './pages/onboardingMarca/PreguntasDeMarca';
import { PrimerasPiezas } from './pages/onboardingMarca/PrimerasPiezas';
import { ConectarCanal } from './pages/onboardingMarca/ConectarCanal';
import { MarcaLista } from './pages/onboardingMarca/MarcaLista';
import { DescripcionDeMarcaONueva } from './pages/onboardingMarca/DescripcionDeMarcaONueva';
import { MARCA_NUEVA } from './lib/onboardingMarca/marcaNueva';
import { onboardingDeMarcaActivo } from './lib/onboardingMarca/flag';
import { limpiarEstado } from './lib/onboardingMarca/estado';

// El onboarding de marca reemplaza al cuestionario cuando el flag está prendido
// (ADR-2026-09-28-onboarding-v2-etapa-1). Se lee una vez: las rutas se arman con esto.
const ONBOARDING_DE_MARCA = onboardingDeMarcaActivo();
import { UnirmeAEquipo } from './pages/UnirmeAEquipo';
import { leerInvitacionPendiente } from './lib/invitacionPendiente';
import { rutaUnirme } from './lib/equipoApi';
import { OwnerOnboarding } from './pages/OwnerOnboarding';
import { RevisarMarca } from './pages/RevisarMarca';
import { MarketingPlanChat } from './pages/MarketingPlanChat';
import { MarketingPlanReview } from './pages/MarketingPlanReview';
import { CalendarRouter } from './pages/CalendarRouter';
import { Ayuda } from './pages/Ayuda';
import { Referidos } from './pages/Referidos';
import { Notificaciones } from './pages/Notificaciones';
import { Modos } from './pages/Modos';
import {
  AtlasAcademy,
  CursoDetalle,
  PantallaExamen,
  PantallaRevision,
  SoloConExamen,
} from './pages/academia';
import { MiMarca } from './pages/MiMarca';
import EstudioMarketingLayout from './pages/estudio-marketing/EstudioMarketingLayout';
import MapaPage from './pages/estudio-marketing/MapaPage';
import PlanPage from './pages/estudio-marketing/PlanPage';
import PiezaPage from './pages/estudio-marketing/PiezaPage';
import AnalisisDatosPage from './pages/estudio-marketing/AnalisisDatosPage';
import InsightsPage from './pages/estudio-marketing/InsightsPage';
import EstrategiaPage from './pages/estudio-marketing/EstrategiaPage';
import MarcaPage from './pages/estudio-marketing/MarcaPage';
import ConfiguracionDatosPage from './pages/estudio-marketing/ConfiguracionDatosPage';
import PuestaEnMarchaPage from './pages/estudio-marketing/PuestaEnMarchaPage';
import Paywall from './pages/Paywall';
import { ResetPassword } from './pages/ResetPassword';
import VerificarCelular from './pages/VerificarCelular';
import { debeVerificarCelular } from './lib/phoneGate';
import { CONFIGCAT_PHONE_GATE_KEY } from './config';
import { getConfigCatClient } from './lib/configcat';
import SeleccionarProductosAnuncio from './pages/SeleccionarProductosAnuncio';
import { EditarImagen } from './pages/EditarImagen';
import EdicionGrafica from './pages/edicionGrafica/EdicionGrafica';
import EdicionGraficaLab from './pages/edicionGrafica/EdicionGraficaLab';
import WatermarkLab from './pages/WatermarkLab';
import { EditarVideo } from './pages/EditarVideo';
import { EditarVideoIAHistorial } from './pages/EditarVideoIAHistorial';
import GeneratedImagesPage from './pages/GeneratedImagesPage';
import SeleccionarTextoEstudioVirtual from './pages/nuevo_modo/SeleccionarTextoEstudioVirtual';
import { SeleccionarPoseModelo } from './pages/SeleccionarPoseModelo';
import { TestV1 } from './pages/TestV1';
import InterfazV2 from './pages/v2/InterfazV2';
// Flujos se carga diferido: es de Pro/Ultra (ADR-2026-09-14-flujos-es-de-pro-y-ultra) y trae React Flow + dagre (~166 kB gzip)
// que los usuarios de producción no tienen por qué descargar.
const FlujosPage = lazy(() => import('./pages/flujos/FlujosPage').then((m) => ({ default: m.FlujosPage })));
const FlujoEditor = lazy(() => import('./pages/flujos/FlujoEditor').then((m) => ({ default: m.FlujoEditor })));
// Laboratorio del analizador de fotos (sólo dev testers; la página hace su propio gate).
const AnalizadorFotosLab = lazy(() => import('./pages/dev/AnalizadorFotosLab'));
import InterfazV2Plantillas from './pages/v2/InterfazV2Plantillas';
import ColeccionDetalle from './pages/v2/ColeccionDetalle';
// El modo Plantillas de Belleza y Cuidado Personal — copia de las dos pantallas de
// arriba, con rutas propias (ADR-2026-09-10-plantillas-belleza-modo-propio).
import PlantillasBelleza from './pages/v2/PlantillasBelleza';
import ColeccionDetalleBelleza from './pages/v2/ColeccionDetalleBelleza';
import ShortsStudioMode from './pages/v2/modes/ShortsStudioMode';
import AnimarImagenMode from './pages/v2/modes/AnimarImagenMode';
import CarruselMode from './pages/v2/modes/CarruselMode';
import MostrarComoFuncionaMode from './pages/v2/modes/MostrarComoFuncionaMode';
import VariantesAcabadoMode from './pages/v2/modes/VariantesAcabadoMode';
import AmbienteCompletoMode from './pages/v2/modes/AmbienteCompletoMode';
// "Mostrar cómo funciona" para Belleza y Cuidado Personal — el hermano del modo homónimo
// de Hogar y Decoración (ADR-2026-09-10-modo-mostrar-como-funciona-belleza).
import MostrarComoFuncionaBellezaMode from './pages/v2/modes/MostrarComoFuncionaBellezaMode';
// "Replicar una foto" — el otro modo de Belleza y Cuidado Personal: el usuario trae la
// escena y Atlas le mete su producto (ADR-2026-09-10-modo-replicar-foto-belleza).
import ReplicarFotoMode from './pages/v2/modes/ReplicarFotoMode';
// "Diseño de indumentaria" — pantalla propia con dos rutas, gate de rubro adentro
// (ADR-2026-09-17-un-diseno-es-un-chat).
import DisenoIndumentariaMode from './pages/v2/modes/disenoIndumentaria/DisenoIndumentariaMode';
import RedesMode from './pages/v2/modes/redes/RedesMode';
import AtajoImagenMode from './pages/v2/modes/AtajoImagenMode';
import PruebaNuevaInterfaz from './pages/nuevo_modo/PruebaNuevaInterfaz';
import PersonalizarModeloEstudioVirtual from './pages/nuevo_modo/PersonalizarModeloEstudioVirtual';
import AnadirAEscena from './pages/nuevo_modo/AnadirAEscena';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import Checkout from './pages/Checkout';
import EnterpriseCheckout from './pages/EnterpriseCheckout';
import DisenarVideo from './pages/DisenarVideo';
import ConfigurarVideoInfluencer from './pages/ConfigurarVideoInfluencer';
import ConfigurarVideoInfluencerNuevo from './pages/ConfigurarVideoInfluencerNuevo';
import ConfigurarVideoInfluencerDetalle from './pages/ConfigurarVideoInfluencerDetalle';
import ConfigurarVideoInfluencerVideo from './pages/ConfigurarVideoInfluencerVideo';
import ConfigurarVideoPersonalizado from './pages/ConfigurarVideoPersonalizado';
import ConfigurarVideoCinematografico from './pages/ConfigurarVideoCinematografico';
import PlanificadorVideoCinematografico from './pages/PlanificadorVideoCinematografico';
import GeneratedVideoPage from './pages/GeneratedVideoPage';
import VideoEditor from './pages/VideoEditor';
import FacturacionPortal from './pages/FacturacionPortal';
import FacturacionOferta from './pages/FacturacionOferta';
import CapacitacionUltra from './pages/CapacitacionUltra';
import FacturaDetalle from './pages/FacturaDetalle';
import VideoProjects from './pages/VideoProjects';
import { mergeLastCrearAnuncioState } from './utils/crearAnuncioStorage';
import UICatalog from './pages/UICatalog';
import UICatalog2 from './pages/UICatalog2';
import {
  DesignSystemLayout,
  DSGettingStartedPage,
  DSTypographyPage,
  DSColorsPage,
  DSSpacingPage,
  DSShadowsPage,
  DSRadiusPage,
  DSSemanticColorsPage,
  DSIconsPage,
  dsFirstPagePath,
} from './pages/design-system';
// Preview de pricing SÓLO en desarrollo (ADR-2026-07-29-ruta-preview-paywall-solo-desarrollo). En el build de producción
// `import.meta.env.DEV` es `false`, la rama de la ruta se elimina y este módulo
// queda sin referencias → Rollup lo saca del bundle.
import PricingDevPreview from './pages/PricingDevPreview';
// Todos los carteles de home juntos — SÓLO desarrollo, misma poda que el de pricing.
import HomeBannersDevPreview from './pages/HomeBannersDevPreview';
// Preview de estilos de interfaz — SÓLO desarrollo, misma poda que el de pricing
// (ADR-2026-07-30-tres-estilos-interfaz-shells-propios y ADR-2026-07-30-rutas-solo-dev-indice-pastilla). Tres shells distintos sobre el mismo contenido real de Atlas.
import { EstiloMinimal } from './pages/estilos';
// Bypass del onboarding para probar en local (ADR-2026-07-30-bypass-dev-onboarding-entrar-app). En producción
// `saltearOnboarding()` es constante `false` y los guards quedan intactos.
import {
  aplicarSkipOnboardingDeLaURL,
  registrarHelperDeConsola,
  saltearOnboarding,
} from './lib/devSkipOnboarding';
// Dar por hechos pasos de la configuración de cuenta paga sin completarlos —
// SÓLO desarrollo, misma poda que el bypass de arriba.
import {
  aplicarSetupHechoDeLaURL,
  registrarHelperDeSetupEnConsola,
} from './lib/devHomeSetup';
import { clasesPageFade, usePageFade } from './hooks/usePageFade';
import { useShellGuards } from './hooks/useShellGuards';
import { Toaster } from 'sileo';

// Se ejecuta al importar el módulo, antes del primer render: el guard de perfil
// redirige con `window.location.href` y no llegaría a ver un flag escrito en un
// efecto.
aplicarSkipOnboardingDeLaURL();
registrarHelperDeConsola();
aplicarSetupHechoDeLaURL();
registrarHelperDeSetupEnConsola();
// AddToHomescreenPrompt is triggered manually from Settings page


// Using the new Welcome component instead of placeholder

function MaintenanceScreen() {
  const whatsappUrl = 'https://wa.me/5491122635392';
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(whatsappUrl)}`;
  return (
    <div className="min-h-screen bg-[#EFF0F5] flex items-center justify-center p-6">
      <div className="animate-fade-in w-full max-w-md rounded-lg bg-white p-8 text-center shadow-lg ring-1 ring-neutral-200 sm:p-10">
        {/* Estado */}
        <div className="inline-flex items-center gap-2 rounded-full bg-warning-soft px-3 py-1">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-warning" />
          </span>
          <span className="text-[length:var(--text-caption)] font-semibold uppercase tracking-wide text-warning-text">
            En mantenimiento
          </span>
        </div>

        {/* Ícono */}
        <div className="mx-auto mt-6 flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary">
          <Wrench className="h-7 w-7 text-white" strokeWidth={2} />
        </div>

        {/* Título */}
        <h1 className="mt-6 text-[22px] font-semibold leading-tight text-brand-black sm:text-[24px]">
          Disculpá por el inconveniente
        </h1>

        <p className="mt-3 text-[length:var(--text-body)] font-medium text-brand-black">
          Estamos haciendo mejoras en los servidores.
        </p>
        <p className="mt-1.5 text-[length:var(--text-body)] text-neutral-600">
          Volvé en unos minutos y vas a poder seguir generando con normalidad.
        </p>

        <div className="my-7 h-px bg-neutral-200" />

        {/* Contacto */}
        <p className="text-[length:var(--text-body)] text-neutral-600">
          ¿No podés esperar? Escribinos y te contamos en cuánto estará todo listo.
        </p>

        {/* Mobile: botón WhatsApp */}
        <div className="mt-5 sm:hidden">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-full bg-brand-primary px-6 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2"
          >
            Escribinos por WhatsApp
          </a>
        </div>

        {/* Desktop: QR */}
        <div className="mt-6 hidden flex-col items-center sm:flex">
          <div className="rounded-md border border-neutral-200 bg-white p-3 shadow-sm">
            <img
              src={qrUrl}
              alt="Código QR para escribirnos por WhatsApp"
              className="h-40 w-40"
            />
          </div>
          <p className="mt-3 text-[length:var(--text-caption)] text-neutral-400">
            Escaneá el código para escribirnos por WhatsApp
          </p>
        </div>
      </div>
    </div>
  );
}

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // State for WelcomeModal is kept but unused while the modal is disabled
  // const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  
  // State for UpdateInfoModal
  const [showUpdateInfoModal, setShowUpdateInfoModal] = useState(false);
  const [updateMessage, setUpdateMessage] = useState<any>(null);
  const [userId, setUserId] = useState<string>('');
  // Poller control for update_disponible
  const updateCheckerStartedRef = useRef(false);
  const updateChannelRef = useRef<any>(null);
  const DEBUG_UPDATE = false;

  // Maintenance mode (global) — ÚNICA fuente: ConfigCat (NO Supabase, ADR-135).
  // El hook lee el flag por CDN, sobrevive caídas de Supabase y es fail-open.
  const maintenance = useMaintenanceFlag();
  // Company id cached from profile
  const [companyId, setCompanyId] = useState<string | null>(null);
  // Circuit breaker (503) state. `breakerOpen` = banner; `breakerHardOpen` =
  // varios ciclos de fallo seguidos → pantalla completa (misma que mantenimiento).
  // Ver ADR-136.
  const [breakerOpen, setBreakerOpen] = useState(false);
  const [breakerHardOpen, setBreakerHardOpen] = useState(false);
  const [offline, setOffline] = useState<boolean>(typeof navigator !== 'undefined' ? !navigator.onLine : false);

  useEffect(() => {
    const st = getCircuitState();
    setBreakerOpen(st.open);
    setBreakerHardOpen(st.hard);
    const off = onCircuitChange((s) => {
      setBreakerOpen(s.open);
      setBreakerHardOpen(s.hard);
    });
    return () => { try { off(); } catch {} };
  }, []);

  // Recuperación del hard-open: con la pantalla completa puesta, los children se
  // desmontan y dejan de pegarle a Supabase, así que nada detectaría que volvió.
  // Este probe le pega directo (salteando el breaker) cada 7s; si Supabase
  // responde, `resetCircuit` baja el hard-open y la app vuelve. Ver ADR-136.
  useEffect(() => {
    if (!breakerHardOpen) return;
    let alive = true;
    const probe = async () => {
      try {
        const { error } = await supabase
          .from('config_system_atlas')
          .select('id')
          .limit(1);
        // "Recuperado" = Supabase respondió algo que NO sea 5xx. Una respuesta
        // 2xx (o incluso un 4xx de RLS) significa que el server está vivo; solo un
        // 5xx o un throw de red cuentan como "sigue caído". Evita que la pantalla
        // quede pegada si el probe recibe un error que no es de infraestructura.
        const infraDown = !!error && Number((error as any)?.status) >= 500;
        if (alive && !infraDown) resetCircuit();
      } catch {
        /* throw = problema de red → sigue caído */
      }
    };
    probe();
    const id = window.setInterval(probe, 7000);
    return () => { alive = false; clearInterval(id); };
  }, [breakerHardOpen]);

  useEffect(() => {
    const update = () => setOffline(typeof navigator !== 'undefined' ? !navigator.onLine : false);
    update();
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  const refreshRubroCache = async (uid: string) => {
    try {
      const { data: profile } = await retrySupabase(async () =>
        await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', uid)
          .single()
      );
      const idCompany: string | null = (profile as any)?.id_company ?? null;
      if (!idCompany) return;
      const { data: company } = await retrySupabase(async () =>
        await supabase
          .from('company_profiles')
          .select('rubro')
          .eq('id_company', idCompany)
          .single()
      );
      // Sólo un rubro de verdad: este cache lo leen las pantallas viejas de anuncios y
      // el centinela les anulaba el respaldo a Moda. Esto corre en cada arranque de
      // sesión, así que es el que de verdad mantiene el cache limpio.
      const rubro = rubroVisible((company as any)?.rubro as string | undefined);
      if (rubro) mergeLastCrearAnuncioState({ rubro });
    } catch {}
  };

  useEffect(() => {
    // Detect if the URL contains an OAuth hash fragment (implicit flow)
    const hasOAuthHash = window.location.hash?.includes('access_token');
    console.log('[Auth Debug] URL:', window.location.href);
    console.log('[Auth Debug] Hash:', window.location.hash?.substring(0, 80));
    console.log('[Auth Debug] hasOAuthHash:', hasOAuthHash);

    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('[Auth Debug] getSession result:', session ? `user=${session.user.id}` : 'null');
      setCacheOwner(session?.user?.id ?? null);
      setSession(session);
      if (session) {
        identifyUser(session.user.id, session.user.email);
        checkUserProfile(session.user);
        refreshRubroCache(session.user.id);
      } else if (!hasOAuthHash) {
        // Only stop loading if there's no pending OAuth redirect to process
        setLoading(false);
      }
      // If hasOAuthHash && !session, keep loading — onAuthStateChange will resolve it
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Auth Debug] onAuthStateChange:', event, session ? `user=${session.user.id}` : 'null');
      // Antes que NADA: el cache de sesión sólo le sirve a quien lo llenó
      // (ADR-2026-08-20-el-cache-de-sesion-cruzaba-cuentas). Va acá, y no sólo en
      // `lib/cerrarSesion`, porque este listener es el único que ve TODOS los
      // cambios de sesión — incluido el caso sin logout de por medio: una sesión
      // que cambia de dueño en otra pestaña, o un login sobre una sesión viva.
      //
      // Por lo mismo, el onboarding de marca a medio hacer de A no le queda a B cuando B
      // entra sobre la sesión viva de A. El logout ya lo borra en `cerrarSesion`
      // (ADR-2026-10-07-el-onboarding-a-medio-hacer-es-de-una-cuenta).
      if (cacheOwner() && session?.user?.id && cacheOwner() !== session.user.id) limpiarEstado();
      // Y por eso mismo el flag del cuestionario se apaga ACÁ, con el cambio de dueño, y
      // no sólo en SIGNED_OUT: si la cuenta A tenía el cuestionario pendiente y la B
      // entra sin logout de por medio, el flag de A (que es de módulo, no de usuario) le
      // quedaba puesto a B, y el guardia la mandaba al cuestionario de otra. La lectura
      // de B vuelve a prenderlo si a B también le corresponde. Greptile PR#135.
      if (cacheOwner() !== (session?.user?.id ?? null)) cuestionarioCompletado();
      setCacheOwner(session?.user?.id ?? null);
      setSession(session);
      if (event === 'SIGNED_IN' && session?.user?.id) {
        identifyUser(session.user.id, session.user.email);
        checkUserProfile(session.user);
        refreshRubroCache(session.user.id);
      }
      // Al desloguear cortamos el vínculo con el usuario: si en el mismo
      // navegador entra otra cuenta, sus eventos no se le atribuyen a la
      // anterior.
      if (event === 'SIGNED_OUT') {
        resetPostHog();
        // Sin sesión no hay cuestionario que deber; la próxima carga vuelve a leer.
        cuestionarioCompletado();
      }
      // If we were waiting for OAuth hash processing and it failed, stop loading
      if (hasOAuthHash && !session) {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkUserProfile = async (user: User) => {
    const userId = user.id;
    try {
      // Store userId for later use with update info modal
      setUserId(userId);

      // Gate de verificación de celular (anti-abuso del trial): cuentas NUEVAS
      // (post PHONE_GATE_CUTOFF) sin celular verificado no entran — primero
      // verifican, después configuran (corre ANTES del redirect al wizard).
      // phone_confirmed_at viene de auth.users: solo Supabase Auth lo escribe, el
      // cliente no puede forjarlo. Kill-switch: flag ConfigCat, FAIL-OPEN (ante
      // cualquier error queda false y el gate no bloquea altas reales).
      // Ver ADR-2026-08-31-verificacion-celular-supabase.
      let gateActivo = false;
      try {
        gateActivo =
          (await getConfigCatClient().getValueAsync(CONFIGCAT_PHONE_GATE_KEY, false)) === true;
      } catch {
        /* fail-open */
      }
      // La sesión pudo cambiar de dueño durante el await (login sobre una sesión
      // viva, u otra pestaña — los casos que el listener de arriba cubre). El
      // veredicto es sobre el usuario CAPTURADO: aplicárselo al dueño nuevo lo
      // mandaría a verificar el celular de otra cuenta. Mismo idioma que el
      // `owner` de setCached: identidad tomada antes del await, comparada al
      // volver. El loading solo se apaga en el logout: con dueño nuevo es de la
      // invocación de ÉL, que sigue decidiendo (gate/empresa/wizard).
      {
        const duenoActual = cacheOwner();
        if (duenoActual !== userId) {
          if (debeApagarLoadingTrasMismatch(duenoActual)) setLoading(false);
          return;
        }
      }
      if (
        debeVerificarCelular({
          gateActivo,
          createdAt: user.created_at,
          email: user.email,
          // Solo altas con email+contraseña: Google/OAuth exentas (2026-09-01).
          // app_metadata lo escribe SOLO el server de Auth — no forjable.
          provider: typeof user.app_metadata?.provider === 'string' ? user.app_metadata.provider : undefined,
          phoneConfirmedAt: user.phone_confirmed_at ?? null,
          saltearOnboardingActivo: saltearOnboarding(),
          pathname: window.location.pathname,
        })
      ) {
        window.location.href = '/verificar-celular';
        return;
      }

      const { data: profile, error } = await retrySupabase(async () =>
        await supabase
          .from('profiles')
          .select('name, id_company, home_message, home_message_seen')
          .eq('id', userId)
          .single()
      );

      // Mismo chequeo tras el segundo await: el profile es del usuario capturado
      // (la query filtra por `userId`); si la sesión ya es de otro, ni el redirect
      // al wizard ni el companyId le corresponden.
      {
        const duenoActual = cacheOwner();
        if (duenoActual !== userId) {
          if (debeApagarLoadingTrasMismatch(duenoActual)) setLoading(false);
          return;
        }
      }

      if (error) {
        console.error('Error fetching profile:', error);
        setLoading(false);
        return;
      }

      // Resolve canonical company UUID (id_company)
      const idCompany: string | null = (profile as any)?.id_company ?? null;
      setCompanyId(idCompany);

      // Usuario sin empresa (recién registrado por mail o por Google) → el
      // cuestionario de onboarding, que es donde ahora se crea la empresa: al
      // terminar el paso 3, con marca por defecto y país deducido
      // (ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real). El nombre ya no ramifica
      // el destino: viene del registro, así que sin empresa siempre se arranca en el
      // paso 1.
      // Bypass dev (ADR-2026-07-30-bypass-dev-onboarding-entrar-app): entrar a la app sin empresa para probar pantallas
      // internas. Ojo: sin `id_company` todo lo que dependa de la empresa viene
      // vacío; es para mirar UI, no para probar flujos con datos.
      // Excepción para el onboarding EXTERNO de Tiendanube: ese flujo crea la empresa por su
      // cuenta (con el rubro que pide en el registro) y no debe ser sacado al wizard genérico.
      // Ver ADR-2026-08-19-tiendanube-onboarding-externo.
      const enFlujoTiendanube = window.location.pathname.startsWith('/tiendanube');
      // Misma excepción para la invitación a un equipo: el invitado recién registrado NO
      // tiene empresa y justamente no tiene que crear una — va a unirse a la del link.
      // Si volvió por el mail de confirmación (sin `?next=`), el token está en localStorage
      // y se lo manda a la invitación en vez de al cuestionario (spec 2026-09-11 §3.3).
      const enFlujoDeInvitacion = window.location.pathname.startsWith('/unirme');
      if (profile && !idCompany && !saltearOnboarding() && !enFlujoTiendanube && !enFlujoDeInvitacion) {
        const tokenPendiente = leerInvitacionPendiente();
        window.location.href = tokenPendiente ? rutaUnirme(tokenPendiente) : ONBOARDING_V2.setup;
        return;
      }

      // Empresa creada pero SIN preguntar nada: es el alta por link, que crea la
      // empresa al registrarse para poder cobrar en un paso y manda al cuestionario
      // DESPUÉS de pagar (ADR-2026-09-22-el-alta-por-link-cobra-primero-y-pregunta-
      // despues). Mientras el rubro siga en el centinela, la app la devuelve ahí: ya
      // pagó, pero sin rubro el catálogo y el agente no tienen con qué trabajar.
      //
      // Las mismas excepciones que el redirect de arriba (Tiendanube y las
      // invitaciones tienen su propio flujo) más las pantallas del alta y del
      // post-pago: interrumpir a alguien que está pagando, o entre el pago y la oferta
      // de factura, es peor que esperar a la próxima entrada.
      if (profile && idCompany && !saltearOnboarding() && !enFlujoTiendanube && !enFlujoDeInvitacion) {
        // Se LEE siempre (una consulta chica) y se REDIRIGE sólo fuera de las rutas
        // exentas — la lista vive en `rutaExentaDelCuestionario`, compartida con el
        // guardia de navegación para que no se desincronicen. Leer también en las
        // exentas es lo que deja prendido el flag: sin eso, quien arranca la sesión en
        // `/facturacion` y después navega a la app se escapaba del cuestionario
        // (ADR-2026-09-23-el-cuestionario-es-obligatorio-tambien-al-navegar).
        const { data: empresa, error: errorEmpresa } = await supabase
          .from('company_profiles')
          .select('rubro')
          .eq('id_company', idCompany)
          .single();
        if (cacheOwner() !== userId) {
          if (debeApagarLoadingTrasMismatch(cacheOwner())) setLoading(false);
          return; // la sesión cambió de dueño durante el await
        }
        // FALLA CERRADO, y en este caso "cerrado" es NO MANDAR A NADIE al cuestionario.
        // Sin mirar el error, un blip de red o de RLS devuelve `data: null`, el rubro
        // queda `undefined`, `onboardingPendiente` dice que sí y la app saca del medio
        // a un cliente de hace un año para meterlo en un cuestionario que no le
        // corresponde. El estado que hay que probar es el POSITIVO: sólo se redirige
        // cuando se leyó una fila y su rubro es el centinela.
        const rubroLeido = errorEmpresa
          ? null
          : ((empresa as { rubro?: string | null } | null)?.rubro ?? null);
        const pudoLeerse = !errorEmpresa && empresa != null;
        // Toda lectura positiva deja el flag como lo que se leyó: prendido con el
        // centinela, apagado con un rubro real. Un error no lo toca en ninguna
        // dirección. Es la red por si el cambio de dueño se escapara del listener.
        if (pudoLeerse && !onboardingPendiente(rubroLeido)) cuestionarioCompletado();
        if (pudoLeerse && onboardingPendiente(rubroLeido)) {
          marcarCuestionarioPendiente();
          if (!rutaExentaDelCuestionario(window.location.pathname)) {
            window.location.href = ONBOARDING_V2.setup;
            return;
          }
        }
      }

      // If no profile issues, proceed to dashboard
      setLoading(false);
      
      // Modal de novedades de la app — SOLO para planes pagos
      // (ADR-2026-08-03-novedades-solo-planes-pagos). A alguien en la prueba gratis se
      // le interrumpe la primera sesión para contarle mejoras de un producto que
      // todavía no compró; el momento de esa cuenta es probar, no ponerse al día.
      //
      // La consulta del plan corre sólo cuando HAY mensaje pendiente, así el resto de
      // las sesiones no paga una consulta extra. Falla cerrado: si no se puede
      // resolver el plan, no se muestra (mejor omitir una novedad que mostrársela a
      // quien no corresponde).
      if (profile.home_message && profile.home_message_seen === false && idCompany) {
        const { data: companyPlan } = await supabase
          .from('company_profiles')
          .select('plan')
          .eq('id_company', idCompany)
          .single();
        const plan = (companyPlan as { plan?: string | null } | null)?.plan ?? null;
        if (cacheOwner() !== userId) return; // tercer await: el modal sería de otra cuenta
        if (plan && plan !== 'gratis') {
          // home_message is already a JS object since it's a jsonb column in PostgreSQL
          setUpdateMessage(profile.home_message);
          setShowUpdateInfoModal(true);
        }
      }
    } catch (err) {
      console.error('Error checking user profile:', err);
      setLoading(false);
    }
  };

  // Refresco remoto de la app: cuando `company_profiles.update_disponible` pasa a
  // `true`, esta pestaña se recarga sola para tomar la versión nueva.
  //
  // NO es un poll por intervalo (el comentario viejo decía "periodically" y no era
  // cierto): se entera por tres vías —un chequeo al montar, la suscripción realtime a
  // la fila de la empresa, y un re-chequeo al volver a la pestaña (visibilitychange /
  // focus / online, con 2s de debounce)—. Antes de recargar apaga el flag, best-effort.
  useEffect(() => {
    if (!session || !companyId || updateCheckerStartedRef.current) return;
    updateCheckerStartedRef.current = true;
    let isMounted = true;
    // Handlers for immediate re-checks
    let visibilityHandler: (() => void) | null = null;
    let focusHandler: (() => void) | null = null;
    let onlineHandler: (() => void) | null = null;

    const setupUpdateChecker = async () => {
      try {
        if (DEBUG_UPDATE) console.log('[UpdateCheck] companyId (cached):', companyId);
        if (!isMounted) return;

        const checkAndHandleUpdate = async () => {
          try {
            const { data: companyRow, error: companyErr } = await retrySupabase(async () =>
              await supabase
                .from('company_profiles')
                .select('update_disponible')
                .eq('id_company', companyId)
                .single()
            );
            if (DEBUG_UPDATE && companyErr) console.warn('[UpdateCheck] company select error:', companyErr);
            if (DEBUG_UPDATE) console.log('[UpdateCheck] polled update_disponible:', (companyRow as any)?.update_disponible);
            const updateDisponible = (companyRow as any)?.update_disponible === true;
            if (updateDisponible) {
              // Attempt to reset flag before redirecting (best-effort)
              try {
                await supabase
                  .from('company_profiles')
                  .update({ update_disponible: false })
                  .eq('id_company', companyId);
                if (DEBUG_UPDATE) console.log('[UpdateCheck] reset update_disponible to false');
              } catch (_e) {
                // Ignore errors; proceed with redirect
                if (DEBUG_UPDATE) console.warn('[UpdateCheck] failed to reset flag, proceeding to redirect');
              }
              // Reload current page (keeps route) to apply update without moving tabs
              if (DEBUG_UPDATE) console.log('[UpdateCheck] reloading current page');
              window.location.reload();
            }
          } catch (_e) {
            // Silently ignore
            if (DEBUG_UPDATE) console.warn('[UpdateCheck] polling threw exception', _e);
          }
        };

        // Initial update check. El flag de mantenimiento lo maneja
        // useMaintenanceFlag (ConfigCat), ya no Supabase — ver ADR-135.
        await checkAndHandleUpdate();

        // Realtime subscription for immediate updates
        try {
          // Clean previous channel if any
          if (updateChannelRef.current) {
            try { await supabase.removeChannel(updateChannelRef.current); } catch {}
            updateChannelRef.current = null;
          }
          const channel = supabase
            .channel(`company-update-${companyId}`)
            .on(
              'postgres_changes',
              { event: 'UPDATE', schema: 'public', table: 'company_profiles', filter: `id_company=eq.${companyId}` },
              (payload) => {
                if (DEBUG_UPDATE) console.log('[UpdateCheck] realtime payload:', payload);
                const newRow: any = (payload as any)?.new;
                if (newRow && newRow.update_disponible === true) {
                  // Best-effort reset then redirect
                  (async () => {
                    try {
                      await supabase
                        .from('company_profiles')
                        .update({ update_disponible: false })
                        .eq('id_company', companyId);
                      if (DEBUG_UPDATE) console.log('[UpdateCheck] realtime reset flag to false');
                    } catch {}
                    if (DEBUG_UPDATE) console.log('[UpdateCheck] realtime reload current page');
                    window.location.reload();
                  })();
                }
              }
            )
            .subscribe();
          updateChannelRef.current = channel;
          if (DEBUG_UPDATE) console.log('[UpdateCheck] realtime subscription started for company', companyId);
        } catch (_e) {
          // If realtime fails, polling still works
          if (DEBUG_UPDATE) console.warn('[UpdateCheck] realtime subscription failed', _e);
        }
        // Add event-driven immediate checks
        let running = false;
        let debounceTimer: number | null = null;
        const safeCheck = async () => {
          if (running) return;
          running = true;
          try { await checkAndHandleUpdate(); } finally { running = false; }
        };
        visibilityHandler = () => {
          if (document.visibilityState !== 'visible') return;
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = window.setTimeout(safeCheck, 2000) as unknown as number;
        };
        focusHandler = () => {
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = window.setTimeout(safeCheck, 2000) as unknown as number;
        };
        onlineHandler = () => {
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = window.setTimeout(safeCheck, 2000) as unknown as number;
        };
        document.addEventListener('visibilitychange', visibilityHandler);
        window.addEventListener('focus', focusHandler as unknown as EventListener);
        window.addEventListener('online', onlineHandler as unknown as EventListener);
      } catch (_e) {
        // Ignore setup errors
      }
    };

    void setupUpdateChecker();

    return () => {
      isMounted = false;
      updateCheckerStartedRef.current = false;
      // Cleanup realtime channel
      if (updateChannelRef.current) {
        try {
          supabase.removeChannel(updateChannelRef.current);
          if (DEBUG_UPDATE) console.log('[UpdateCheck] realtime channel removed');
        } catch {}
        updateChannelRef.current = null;
      }
      if (visibilityHandler) document.removeEventListener('visibilitychange', visibilityHandler);
      if (focusHandler) window.removeEventListener('focus', focusHandler as unknown as EventListener);
      if (onlineHandler) window.removeEventListener('online', onlineHandler as unknown as EventListener);
    };
  }, [session, companyId]);

  if (loading) {
    return (
      <>
        {(breakerOpen || offline) && (
          <div className="fixed top-0 left-0 right-0 z-[1000] pointer-events-none">
            <div className="mx-auto max-w-4xl sm:max-w-7xl px-4 pt-2">
              <div className="pointer-events-auto rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-sm px-3 py-2 shadow-sm text-center">
                {offline ? 'Sin conexión a internet. Verificá tu conexión.' : 'Servidores en mantenimiento. Estarán operativos en unos minutos.'}
              </div>
            </div>
          </div>
        )}
        <div className="min-h-screen bg-[#EFF0F5] flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </>
    );
  }

  if (maintenance || breakerHardOpen) {
    return <MaintenanceScreen />;
  }

  // Usuarios sin sesión van al login clásico. Durante un tiempo entraron por el
  // carrusel de bienvenida nuevo (`/bienvenida`, ADR-693), que hoy está en
  // cuarentena hasta terminar la migración
  // (ADR-2026-07-31-onboarding-nuevo-en-cuarentena).
  if (!session) return <Navigate to="/auth" />;

  return (
    <>
      {(breakerOpen || offline) && (
        <div className="fixed top-0 left-0 right-0 z-[1000] pointer-events-none">
          <div className="mx-auto max-w-4xl sm:max-w-7xl px-4 pt-2">
            <div className="pointer-events-auto rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-sm px-3 py-2 shadow-sm text-center">
              {offline ? 'Sin conexión a internet. Verificá tu conexión.' : 'Servidores en mantenimiento. Estarán operativos en unos minutos.'}
            </div>
          </div>
        </div>
      )}
      {children}
      {/* WelcomeModal disabled as requested but kept in codebase */}
      {/* <WelcomeModal isOpen={showWelcomeModal} onClose={() => setShowWelcomeModal(false)} /> */}

      {updateMessage && (
        <UpdateInfoModal
          isOpen={showUpdateInfoModal}
          onClose={() => setShowUpdateInfoModal(false)}
          message={updateMessage}
          userId={userId}
        />
      )}

      {/* Anuncios de funcionalidad nueva — ADR-2026-08-31-anuncios-de-funcionalidad.
          Va acá, en `ProtectedRoute`, porque es el único lugar que se monta UNA vez
          y sobrevive a toda la navegación autenticada: montado en el shell nuevo
          quedaría afuera de las pantallas que todavía usan `DashboardLayout`, y
          montado en cada pantalla habría que acordarse en cada una.

          Decide solo: mira el catálogo, la ruta y qué ya cerró el usuario. Si no hay
          nada vigente no dibuja ni un nodo. */}
      <AnuncioModal />
    </>
  );
};
// Landing inicial de la ruta `/`.
//
// DESKTOP va siempre a `/interfaz-v2`, la home de la plataforma: desde
// ADR-2026-07-31-inicio-se-funde-interfaz-v2 es la que trae el diseño nuevo Y toda la
// lógica (modos, gating por plan, sheets, plantillas). El grupo de testeo dejó de
// decidir acá — el diseño nuevo es para todos.
//
// MOBILE sigue decidiendo por grupo, porque el flujo clásico es el producto del Grupo A
// y de las cuentas sin grupo: ésas van a seleccionar productos. Dev y Grupo B arrancan en
// Interfaz V2 — el dev iba al clásico y aterrizaba en la pantalla vieja
// (ADR-2026-09-10-el-landing-de-mobile-no-manda-al-dev-al-flujo-viejo).
//
// Decide de inmediato con el valor cacheado (no bloquea esperando el fetch): así un
// usuario deslogueado cae en el flujo clásico → ProtectedRoute → /auth, sin quedar
// colgado. Para usuarios recurrentes el cache de `useUserGroup` hace el destino
// correcto al instante.
const HomeRedirect = () => {
  const { isDevTester, grupo } = useUserGroup();
  const isDesktop = useIsDesktop();
  if (isDesktop) return <Navigate to="/interfaz-v2" replace />;
  const to = landingIsV2(isDevTester, grupo)
    ? '/interfaz-v2'
    : '/redes-sociales/crear-anuncio/seleccionar-productos';
  return <Navigate to={to} replace />;
};

// Layout with sidebar on desktop
const DashboardLayout = ({
  children,
  navInferior,
}: {
  children: React.ReactNode;
  /**
   * Qué barra de navegación inferior montar en mobile —
   * ADR-2026-08-04-la-barra-inferior-vive-en-el-shell.
   *
   * Por defecto la vieja (`MobileBottomNav`, 4 tabs, sólo en las pantallas-tab de
   * `MOBILE_BOTTOM_NAV_PATHS`). `ShellMobile` le pasa la nueva —cinco lugares con el
   * botón de Generar en el medio— para las pantallas del rediseño. Es una prop y no
   * una regla por ruta acá adentro porque la barra nueva necesita datos (los modos,
   * el rubro de la marca) que este layout no tiene ni tiene por qué buscar.
   */
  navInferior?: React.ReactNode;
}) => {
  const { sidebarWidth } = useSidebar();
  // Cuánto EMPUJA el chat desde la derecha. Es el mismo número con el que se
  // dibuja `<PanelChatAtlas />` (los dos salen de `useEmpujeChat`), así que la
  // franja del chat y el hueco que le deja el contenido no pueden desfasarse.
  // 0 cuando el chat está cerrado, y también en mobile: ahí el chat va a
  // pantalla completa POR ENCIMA y el contenido no se mueve.
  const { ancho: anchoChat } = useEmpujeChat();
  // Fade de entrada al cambiar de página, para toda la app (ADR-2026-07-31-fade-entre-paginas-toda-app).
  const pageFadeVisible = usePageFade();
  // Track mobile menu state via custom event and compute panel width + viewport
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [panelWidth, setPanelWidth] = React.useState(0);
  const [isMobileView, setIsMobileView] = React.useState(false);
  const [hideMenuButton, setHideMenuButton] = React.useState(false);

  // Listen for hide-menu-button events from child components
  React.useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as any;
        if (typeof detail?.hide === 'boolean') {
          setHideMenuButton(detail.hide);
        }
      } catch {
        // noop
      }
    };
    window.addEventListener('hide-menu-button' as any, handler as EventListener);
    return () => window.removeEventListener('hide-menu-button' as any, handler as EventListener);
  }, []);

  React.useEffect(() => {
    const computePanelWidth = () => {
      const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
      const width = Math.min(320, Math.round(vw * 0.82));
      setPanelWidth(width);
    };
    const updateBreakpoint = () => {
      const isMobile = typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches === false : true;
      setIsMobileView(isMobile);
    };
    computePanelWidth();
    updateBreakpoint();
    window.addEventListener('resize', computePanelWidth);
    window.addEventListener('resize', updateBreakpoint);
    return () => {
      window.removeEventListener('resize', computePanelWidth);
      window.removeEventListener('resize', updateBreakpoint);
    };
  }, []);

  React.useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as any;
        if (typeof detail?.isMobileMenuOpen === 'boolean') {
          setIsMobileMenuOpen(detail.isMobileMenuOpen);
        }
      } catch {
        // noop
      }
    };
    window.addEventListener('sidebar-mobile-toggle' as any, handler as EventListener);
    return () => window.removeEventListener('sidebar-mobile-toggle' as any, handler as EventListener);
  }, []);

  // Guard de marca en borrador + reset del scroll global. Vivían inline acá; se
  // mudaron a `useShellGuards` para que el shell nuevo (desktop) corra exactamente
  // los mismos y no puedan divergir — ADR-2026-07-31-shell-nuevo-urls-viejas.
  useShellGuards();

  // OLD shell transform animation disabled — mobile menu now uses dropdown in Sidebar.tsx
  // const translateX = isMobileMenuOpen && isMobileView ? Math.max(0, panelWidth + 40 ) : 0;
  // const contentOffsetY = isMobileMenuOpen && isMobileView ? 40 : 0;
  // const depthZ = isMobileMenuOpen && isMobileView ? -200 : 0;
  // const perspectivePx = 1200;
  // const shellStyle = isMobileMenuOpen && isMobileView ? { transform: `perspective(...)`, ... } : { transform: 'none' };
  const shellStyle: React.CSSProperties = { transform: 'none' };

  // Avoid adding extra top padding on full-screen pages like Home ('/')
  const location = useLocation();
  const isRootPath = location.pathname === '/';
  const isCreateFlow = location.pathname.startsWith('/redes-sociales/crear-anuncio');
  const isEditImage = location.pathname.startsWith('/redes-sociales/editar-imagen');
  const isGeneratedImages = location.pathname.startsWith('/redes-sociales/imagenes-generadas');
  const isGeneratedVideo = location.pathname === '/redes-sociales/video-generado';
  const isGraphicEditor = location.pathname.startsWith('/redes-sociales/edicion-grafica');
  // Pages that need special top padding handling
  const isSpecialTopPadding = isRootPath || isCreateFlow || isEditImage || isGeneratedImages || isGeneratedVideo || isGraphicEditor;
  // Pages that should have NO bottom padding (full-bleed) - exclude crear-anuncio to give it bottom space
  const isFullBleedFlow = isRootPath || isCreateFlow || isEditImage || isGeneratedImages || isGeneratedVideo || isGraphicEditor;
  // En PWA standalone con `apple-mobile-web-app-status-bar-style: black-translucent`
  // la status bar pasa a ser transparente y el WebView se extiende detrás. El
  // header mobile del Sidebar ya tiene `paddingTop: env(safe-area-inset-top)`,
  // así que su altura efectiva crece con el inset — el padding-top del main
  // tiene que sumar ese mismo inset para que el contenido no se quede tapado.
  // `--aviso-generando-mobile`: el header fijo de "generando de fondo" corre al header
  // del shell hacia abajo (lee la variable) y el contenido tiene que bajar lo mismo o
  // queda debajo de él (ADR-2026-09-06-el-aviso-de-generando-de-fondo). Sin aviso vale 0.
  const mainTopPadding = isSpecialTopPadding
    ? 'pt-[calc(env(safe-area-inset-top)+3.5rem+var(--aviso-generando-mobile,0px))] md:pt-0'
    : 'pt-[calc(env(safe-area-inset-top)+3.5rem+0.75rem+var(--aviso-generando-mobile,0px))] md:pt-0';

  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const estudioVirtualMobileNoMenu =
    isMobileView &&
    (
      (
        searchParams.get('estudio_virtual') === '1' &&
        (location.pathname === '/redes-sociales/crear-anuncio/personalizar-modelo-estudio-virtual' ||
          location.pathname === '/redes-sociales/crear-anuncio/seleccionar-pose' ||
          location.pathname === '/redes-sociales/crear-anuncio/seleccionar-texto-estudio-virtual')
      ) ||
      location.pathname === '/subscription'
    );

  /* El reset de `overflow` en cada cambio de ruta también se mudó a
     `useShellGuards` (llamado arriba), por el mismo motivo que el guard de draft. */

  const toggleMobileMenuFromLayout = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    window.dispatchEvent(new CustomEvent('sidebar-mobile-toggle', {
      detail: { isMobileMenuOpen: newState }
    }));
  };

  return (
    <div className="bg-surface-mobile md:bg-[#F3F3F3] min-h-[100svh] md:min-h-screen max-w-[100vw] overflow-x-hidden overflow-y-auto md:overflow-y-hidden flex flex-col">
      <Sidebar />
      {/* El chat es la OTRA columna fija del cascarón: la sidebar clava el
          borde izquierdo del contenido y el chat el derecho. Va acá —hermano
          de `<Sidebar />` y fuera del shell de contenido— y NO por portal,
          justamente porque no se superpone: empuja. Fuera de `DashboardLayout`
          no existe, que es lo correcto (el chat es de la app logueada). */}
      <PanelChatAtlas />
      {/* El flotante que abre ese panel desde cualquier pantalla del layout viejo (desktop).
          Hermano del panel, por lo mismo que el panel
          (ADR-2026-09-10-el-boton-flotante-de-preguntale-a-atlas). */}
      <BotonFlotanteChat />
      {/* Barra de navegación inferior (solo mobile). La de por defecto es la vieja,
          que además se auto-limita a las pantallas-tab; el shell del rediseño pasa
          la suya. */}
      {navInferior ?? <MobileBottomNav />}
      {/* Sombra de la "parte principal" cayendo sobre la nav bar. Es un
          elemento dedicado y NO la sombra del panel de contenido: esa queda
          tapada porque la sidebar (z-30) está por encima del panel (z-auto).
          Acá replicamos la posición + las MISMAS esquinas redondeadas del
          panel para que la sombra SIGA la curva, con z por encima de la
          sidebar. Transparente y sin eventos: sólo proyecta la sombra hacia
          la izquierda (sobre la nav). */}
      <div
        aria-hidden="true"
        className="hidden md:block fixed top-0 right-0 bottom-0 z-[31] pointer-events-none rounded-tl-[26px] rounded-bl-[26px] shadow-[-14px_0_30px_-18px_rgba(2,6,43,0.16)] transition-[left] duration-300 ease-out"
        style={{ left: sidebarWidth }}
      />
      {/* Esquinas redondeadas del panel (desktop) — pintadas como OVERLAY.
          Antes la curva era un clip-path sobre el propio panel fixed que
          scrollea, y eso obligaba al compositor a enmascarar TODAS las capas
          GPU hijas en cada frame → glitches intermitentes al mostrar
          carruseles pesados (fondos/modelos globales). Ver ADR-574. Cada
          pieza es un cuadrado de 26px cuyo radial-gradient pinta el color
          del fondo exterior (#F3F3F3, mismo del contenedor raíz) fuera de la
          curva y deja transparente el cuarto de círculo del panel. Sin
          clip-path ni border-radius sobre capas compuestas: también evita la
          franja oscura de Windows/D3D que motivó el clip-path original. */}
      <div
        aria-hidden="true"
        className="hidden md:block fixed top-0 z-[31] pointer-events-none w-[26px] h-[26px] transition-[left] duration-300 ease-out"
        style={{
          left: sidebarWidth,
          background:
            'radial-gradient(circle 26px at 26px 26px, transparent 25px, #F3F3F3 26px)',
        }}
      />
      <div
        aria-hidden="true"
        className="hidden md:block fixed bottom-0 z-[31] pointer-events-none w-[26px] h-[26px] transition-[left] duration-300 ease-out"
        style={{
          left: sidebarWidth,
          background:
            'radial-gradient(circle 26px at 26px 0px, transparent 25px, #F3F3F3 26px)',
        }}
      />
      {/* OLD hamburger + arrow close buttons disabled — mobile menu now handled by Sidebar.tsx top bar */}
      {/* Transformable shell to mimic screen sliding right and shrinking */}
      <div
        className="relative z-30 md:z-auto transition-transform duration-500 ease-out transform-gpu bg-surface-mobile md:bg-transparent md:min-h-0"
        style={shellStyle}
      >
        <main
          className={`flex-1 main-content overflow-x-hidden ${mainTopPadding} ${isFullBleedFlow ? 'full-bleed' : ''} bg-surface-mobile md:bg-transparent`}
          style={{ maxWidth: '100vw' }}
        >
          {/* Contenido como panel "encima" de la nav gris. En DESKTOP es un
              marco FIJO al viewport con scroll interno: así las esquinas
              izquierdas redondeadas (la curva, no una línea recta) quedan
              siempre visibles arriba y abajo, y nunca se ve "el final" de la
              página. En mobile es full-width plano con scroll de documento. */}
          {/* El fade de entrada (ADR-2026-07-31-fade-entre-paginas-toda-app) va sobre ESTE div, que ya existe, y no
              sobre un wrapper nuevo: un nodo extra cambiaría el bloque contenedor
              de las alturas en porcentaje de todas las pantallas de adentro. La
              sidebar queda afuera del panel, así que no se desvanece. */}
          <div
            // `right` se anima igual que `left`: plegar la sidebar corre el
            // borde izquierdo y abrir el chat corre el derecho, con la misma
            // curva y la misma duración. El chat EMPUJA la pantalla (no la
            // tapa), y esta es la mitad del empujón que le toca al contenido.
            className={`content-panel-clip min-h-full md:min-h-0 md:fixed md:top-0 md:right-0 md:bottom-0 md:overflow-y-auto md:overflow-x-hidden bg-surface-mobile md:bg-surface-content md:transition-[left,right] md:duration-300 md:ease-out ${clasesPageFade(
              pageFadeVisible,
            )}`}
            style={{
              left: isMobileView ? undefined : sidebarWidth,
              right: isMobileView ? undefined : anchoChat,
            }}
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

// Resultados del onboarding: SÓLO en desktop se muestran dentro del shell de la app
// (sidebar) con el layout clásico; en mobile es la pantalla previa (grilla 2×2)
// full-screen, sin shell. `AccountSetupResults` ya elige el layout por viewport; acá
// se agrega/omite el shell en consecuencia. `DashboardLayout` va directo (NO bajo
// `ProtectedRoute`) porque el usuario del cuestionario no tiene empresa aún y el guard
// lo rebotaría a `/configuracion-cuenta`; el sidebar degrada bien sin empresa.
const AccountSetupResultsRoute = ({ devPreview = false }: { devPreview?: boolean }) => {
  const isDesktop = useIsDesktop();
  return isDesktop ? (
    <DashboardLayout>
      <AccountSetupResults devPreview={devPreview} />
    </DashboardLayout>
  ) : (
    <AccountSetupResults devPreview={devPreview} />
  );
};

/**
 * Shell mobile de las pantallas rediseñadas —
 * ADR-2026-08-04-la-barra-inferior-vive-en-el-shell.
 *
 * Es `DashboardLayout` (el chrome de mobile: header flotante, menú de cuenta,
 * geometría) con la barra inferior NUEVA en lugar de la vieja, más el sheet de
 * "Generar" que abre su botón central. Antes esas dos cosas las montaba `InterfazV2`,
 * así que existían sólo en el inicio: al tocar "Catálogo" en la barra la barra
 * desaparecía, y Marca y Creaciones mostraban la vieja, con otros destinos.
 *
 * El sheet vive acá y no en cada pantalla porque su contenido no depende de dónde
 * estés: son los modos de la app, que ahora salen de `useModosGenerar` —el mismo
 * catálogo que `/modos` y el sheet de Mi marca— y abren por URL.
 *
 * No se rehace el layout: `DashboardLayout` ya resuelve el padding por pantalla, los
 * guards y el fade, y las pantallas de adentro son las mismas que en desktop.
 */
const ShellMobile = () => {
  const [sheetGenerar, setSheetGenerar] = React.useState(false);
  // El rubro de la marca ACTIVA decide el segundo lugar de la barra
  // (ADR-2026-08-04-el-segundo-lugar-de-la-barra-sigue-al-rubro) y también si el sheet
  // ofrece Plantillas (ADR-2026-08-04-plantillas-es-una-opcion-de-generar), con el de la
  // empresa como respaldo — la misma fuente y el mismo orden que la home.
  const {
    profile: perfilMarca,
    activeBrand: marcaActiva,
    currentBrandId: marcaActivaId,
  } = useBrandProfile();
  // Cambiar de marca remonta la pantalla, igual que en el shell de escritorio —
  // ADR-2026-09-13-el-cambio-de-marca-no-llega-al-chat. En mobile el switcher vive en el
  // modal de la sidebar vieja y está disponible en TODAS las pantallas, así que la que
  // estuviera abierta se quedaba con los datos de la marca anterior hasta navegar. Sin el
  // fade de escritorio: acá no hay transición entre secciones que respetar.
  const nonceMarca = useNonceDeMarca(marcaActivaId);
  // El rubro crudo, además del booleano de Plantillas: el sheet lo necesita para
  // decidir si entra Infografía (ADR-2026-08-05-infografia-la-ve-el-rubro-hogar-y-decoracion) y
  // con qué carátula se ve Catálogo
  // (ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro).
  // Mientras `useBrandProfile` carga (tres consultas) el rubro es `null`, y con eso los
  // modos de un rubro (`soloModa`, `soloBelleza`, `soloHogarDeco`) faltaban del sheet
  // de Generar hasta que volviera la respuesta: la semilla es el cache que ya mantiene
  // `useRubroMarcaActiva` (ADR-2026-09-18-el-sheet-de-generar-arranca-con-el-rubro-cacheado).
  const [rubroCacheado] = React.useState(rubroMarcaCacheado);
  const rubroMarca = marcaActiva?.rubro ?? perfilMarca?.rubro ?? rubroCacheado;
  // Cuál de los dos modos de Plantillas ve esta marca —el de Moda o el de Belleza y
  // Cuidado Personal (ADR-2026-09-10-plantillas-belleza-modo-propio)—, o ninguno.
  // La barra inferior necesita la RUTA (lleva a una pantalla concreta) y el sheet de
  // Generar sólo si hay o no (adentro elige por rubro), de ahí las dos.
  const rutaPlantillas: string | null = rubroVePlantillas(rubroMarca)
    ? '/interfaz-v2/plantillas'
    : rubroVePlantillasBelleza(rubroMarca)
      ? '/interfaz-v2/plantillas-belleza'
      : null;
  const conPlantillas = rutaPlantillas !== null;
  /**
   * ¿El segundo lugar de la barra es el AGENTE? —
   * ADR-2026-09-22-el-segundo-lugar-de-la-barra-es-el-agente.
   *
   * Es el MISMO predicado que decide cómo abre la home
   * (`homeAbreConElAgente`, ADR-2026-09-20-la-home-de-los-otros-rubros-abre-con-el-agente)
   * y no una lista nueva de rubros: los que abren con el Agente son, por definición, los
   * que no tienen catálogo propio ni plantillas, y sería absurdo que la home les hablara
   * del Agente y la barra los mandara a un modo que no usan. Con un predicado propio, las
   * dos superficies podrían contradecirse.
   *
   * Hogar y Decoración queda afuera a propósito (no abre con el Agente): ahí el segundo
   * lugar sigue siendo Catálogo, que para ese rubro es el de muebles.
   */
  const conAgenteEnLaBarra = homeAbreConElAgente(rubroMarca);
  const { modos, agente, ediciones, ejemplos, cerrarEjemplos, irAPlanes } =
    useModosGenerar({
      conPlantillas,
      rubro: rubroMarca,
    });
  // Las pantallas-tarea van SIN barra —
  // ADR-2026-08-05-la-barra-no-va-en-las-pantallas-tarea. Traen su propia barra de
  // acciones abajo y la de navegación les cortaba los controles por detrás.
  const { pathname } = useLocation();
  const sinBarra = MOBILE_RUTAS_SIN_NAV.some((p) => pathname.startsWith(p));
  // Las rutas que se mudaron al shell nuevo por el desktop
  // (ADR-2026-08-20-la-sidebar-vieja-la-elegia-el-grupo-de-rutas) pasan por acá ahora,
  // pero en mobile tienen que quedar EXACTAMENTE como estaban: la mudanza arregla la
  // sidebar de escritorio y no es una decisión sobre la barra inferior.
  //
  // `undefined` —y no `null`— es lo que las deja igual: `DashboardLayout` cae a
  // `<MobileBottomNav />`, que es la barra vieja y además se auto-limita a las
  // pantallas-tab de `MOBILE_BOTTOM_NAV_PATHS`. Ninguna de ellas está en esa lista,
  // así que siguen sin barra inferior, que es lo que tienen hoy. Con `null` el
  // resultado sería el mismo de casualidad; con la barra NUEVA aparecería una barra
  // que hoy no existe en esas pantallas.
  const conNavVieja = esRutaShellLegacy(pathname);
  return (
    <DashboardLayout
      navInferior={
        conNavVieja ? undefined : sinBarra ? null : (
          <>
            <NavInferiorMobile
              onGenerar={() => setSheetGenerar(true)}
              rutaPlantillas={rutaPlantillas}
              /* El Agente en el segundo lugar, con el MISMO candado de plan que la card
                 del sheet de Generar (de ahí sale `agente`). */
              agente={conAgenteEnLaBarra ? agente : null}
            />
            {sheetGenerar && (
              <SheetGenerar
                modos={modos}
                agente={agente}
                ediciones={ediciones}
                onClose={() => setSheetGenerar(false)}
              />
            )}
            {/* Visor de ejemplos de un modo bloqueado — el tap en una card con
                candado cierra el sheet y abre esto, así que vive AFUERA del
                condicional del sheet (ADR-2026-08-25-sheet-generar-fixes-mobile).
                Mismo visor que la home V2 y /modos, con su mismo CTA. */}
            <ModeExamplesModal
              open={ejemplos !== null}
              onClose={cerrarEjemplos}
              title={ejemplos?.title ?? ''}
              description={ejemplos?.description}
              examples={ejemplos?.examples ?? []}
              actionLabel="Ver planes"
              onAction={irAPlanes}
              locked
              planNote={ejemplos?.planNote}
            />
          </>
        )
      }
    >
      <Outlet key={nonceMarca} />
    </DashboardLayout>
  );
};

/**
 * Shell de las pantallas rediseñadas — ADR-2026-07-31-shell-nuevo-urls-viejas.
 *
 * En DESKTOP monta el shell nuevo (`EstiloMinimal`: sidebar flotante, barra de
 * créditos, fade entre secciones). En MOBILE monta `ShellMobile`, que es el chrome de
 * siempre con la barra inferior del rediseño. Mismo patrón, y por el mismo motivo,
 * que `AccountSetupResultsRoute` acá arriba.
 *
 * La frontera es 768px (`useShellEscritorio`) y no 1024 —
 * ADR-2026-08-05-el-shell-nuevo-arranca-en-768. Con el corte en 1024, la franja de 768 a
 * 1023 caía en `ShellMobile`, que monta el `<aside>` de la app… y ése aparece justamente
 * desde 768: el resultado era la sidebar VIEJA con el contenido nuevo al lado, en toda
 * la app. 768 es el ancho a partir del cual entra una sidebar al costado, que es la
 * pregunta que este shell tiene que responder.
 *
 * Las pantallas de adentro son las MISMAS en los dos casos y no se enteran de cuál
 * las está hospedando: leen la geometría de `SidebarContext`, que cada shell provee
 * con sus propios números.
 *
 * El hook corre contra el viewport, así que redimensionar la ventana cruza de un shell
 * al otro y remonta el árbol de abajo. Es aceptable —nadie usa la app arrastrando el
 * borde de la ventana— y es exactamente lo que ya hace la pantalla de resultados del
 * alta.
 */
const ShellApp = () => {
  const enEscritorio = useShellEscritorio();
  return (
    <>
      {/* Reanuda las generaciones que quedaron corriendo al recargar. Vive en el
          shell y no en `InterfazV2` para correr desde CUALQUIER ruta —también desde
          Creaciones, que es donde el usuario va a mirar— y para no navegar a ningún
          lado (ADR-2026-09-06-registro-de-generaciones-en-curso). */}
      <ReanudadorDeGeneraciones />
      {/* El aviso de "generando de fondo": mismo lugar y mismo motivo que el reanudador
          —desde cualquier ruta— y se calla solo cuando la pantalla ya muestra la carga
          (ADR-2026-09-06-el-aviso-de-generando-de-fondo). */}
      <AvisoGenerandoDeFondo enEscritorio={enEscritorio} />
      {enEscritorio ? <EstiloMinimal /> : <ShellMobile />}
    </>
  );
};

/**
 * Alias de compatibilidad hacia la ruta canónica — ADR-2026-07-31-shell-nuevo-urls-viejas.
 *
 * Las doce pantallas del rediseño se probaron bajo URLs nuevas (`/creaciones`,
 * `/modo/:mode`, `/plantillas`, …). Al aplicarse el diseño sobre las rutas que ya
 * existían, esas URLs quedaron como el atajo que ya está en la cabeza (y en los
 * favoritos) de quien lo estuvo revisando, así que redirigen en vez de desaparecer.
 *
 * Se redirige y NO se monta la pantalla en las dos URLs: con dos rutas vivas para la
 * misma pantalla, la sidebar sólo puede marcar una, y entrar por la otra dejaba el
 * menú sin ningún ítem seleccionado.
 *
 * `search` y `hash` se conservan porque hay flujos que dependen de ellos —
 * `/modo/:mode?preset=…` es cómo se abre una plantilla— y un redirect que los tira
 * rompería justo el caso que el alias intenta salvar.
 */
const AliasRuta = ({
  destino,
}: {
  destino: (params: Readonly<Record<string, string | undefined>>) => string;
}) => {
  const params = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`${destino(params)}${search}${hash}`} replace />;
};

// `/bienvenida` y `/bienvenida/:rubro` → `/auth?mode=signup[&rubro=…]`. Esas URLs se
// publicaron (landings por rubro de ADR-785, PWA instaladas), así que siguen andando.
//
// El **query string original se conserva entero**: si se reemplazara por uno nuevo, un
// `/bienvenida/moda?utm_source=meta&ref=ABC` perdería la campaña y el referido, porque
// `captureAttributionParams()` corre al montar `/auth` y para entonces la URL ya no los
// tendría. Ver ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real.
const BienvenidaCompat = () => {
  const { rubro } = useParams<{ rubro?: string }>();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('mode', 'signup');
  if (rubro) params.set('rubro', rubro);
  return <Navigate to={`/auth?${params.toString()}`} replace />;
};

function App() {
  // Le avisa al backend cuándo el usuario está mirando la app, para no mandarle un push de "tu
  // generación está lista" a alguien que la está viendo en pantalla. Se monta acá y no en el
  // layout autenticado para que cubra TODAS las rutas; sin sesión no hace nada.
  usePresencia();
  // El push de este navegador queda a nombre de la cuenta que entra (si ya estaba activado).
  useSincronizarPush();

  return (
    <MobileBlocker>
      <Toaster position="top-right" theme="light" options={{ fill: '#FAFBFC' }} />
      <Router>
        <ScrollToTop />
        {/* La obligación del cuestionario también al NAVEGAR, no sólo al arrancar la
            sesión (ADR-2026-09-23-el-cuestionario-es-obligatorio-tambien-al-navegar). */}
        <GuardiaDelCuestionario />
        <PermissionProvider>
          <SidebarProvider>
          {/* El Chat con su entorno (identidad, catálogos, los contextos que leen sus
              tarjetas) y el provider, remontado al cambiar de cuenta
              (ADR-2026-09-08-el-chat-toma-el-motor-del-asistente). */}
          <ChatAtlasRaiz>
          <Routes>
            {/* Públicas — sin guard ni layout */}
            {/* Entrada ÚNICA. El modo sale de `?mode=` (signup → carrusel; sin param
                → login) y el rubro de la campaña de `?rubro=`. Contrato de URL
                intacto: hay links externos que no controlamos.
                Ver ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real. */}
            <Route path="/auth" element={<Auth />} />
            {/* Onboarding EXTERNO de Tiendanube (Fase 1): pantalla propia de login/registro,
                aislada del AuthForm. Pública (sin guard): el form se muestra sin sesión.
                Ver ADR-2026-08-19-tiendanube-onboarding-externo. */}
            <Route path="/tiendanube" element={<TiendanubeAuth />} />
            <Route path="/tiendanube/conectar" element={<TiendanubeConectar />} />
            <Route path="/tiendanube/continuar" element={<TiendanubeContinuar />} />
            <Route path="/tiendanube/producto" element={<TiendanubeProducto />} />
            {/* Compatibilidad: `/bienvenida` estuvo publicada (PWA instaladas, links
                de campaña por rubro). El rubro viaja ahora como query param. */}
            <Route path="/bienvenida" element={<BienvenidaCompat />} />
            <Route path="/bienvenida/:rubro" element={<BienvenidaCompat />} />
            <Route path="/grabar-voz" element={<GrabarVoz />} />
            {/* Harness de test masivo de prompts — ruta oculta, solo dev_tester
                (la página redirige a home para cualquier otra cuenta). */}
            <Route path="/test-masivo" element={<TestMasivo />} />
            <Route path="/generar-audio" element={<GenerarAudio />} />
            {/* Grabación del diálogo para el plugin (UXP no accede al micrófono).
                Pública a propósito: la página hace su propio gate y ProtectedRoute
                perdería el ?project_id. Ver ADR-579. */}
            <Route path="/grabar-dialogo" element={<GrabarDialogo />} />
            {/* Registro/login a pantalla completa (mobile). En desktop redirige a
                `/auth`, donde el auth es el panel fijo del split.
                Ver ADR-2026-07-30-registro-pantalla-completa. */}
            <Route path={ONBOARDING_V2.registro} element={<Registro />} />
            {/* Pasos 1–4: rutas hijas de un layout común con header fijo (barra de
                progreso animada) y transición slide/push entre pasos. Ver ADR-736. */}
            {ONBOARDING_DE_MARCA ? (
              /* Onboarding de marca: link → tablero en vivo → primeras piezas → listo.
                 Ver ADR-2026-09-28-onboarding-v2-etapa-1. */
              <Route path={ONBOARDING_V2.setup} element={<OnboardingMarcaLayout />}>
                <Route index element={<LinkDeMarca />} />
                <Route path="preguntas" element={<PreguntasDeMarca />} />
                <Route path="marca" element={<TableroDeMarca />} />
                <Route path="canal" element={<ConectarCanal />} />
                {/* La competencia salió del onboarding (ADR-2026-10-02-onboarding-v2-sin-competencia):
                    quien tenga la URL vieja en el historial sigue a las piezas. */}
                <Route path="competencia" element={<Navigate to={ONBOARDING_V2.setupPiezas} replace />} />
                {/* Los pasos del cuestionario de siempre no existen en este flujo: quien vuelve
                    por el historial, un link guardado o el "reintentar" de
                    `AccountSetupGenerating` (que sigue montado, y manda a `producto` si una
                    generación falla) arranca el onboarding de marca en vez de caer en una ruta
                    que no existe (review Greptile #164). */}
                <Route path="categoria" element={<Navigate to={ONBOARDING_V2.setup} replace />} />
                <Route path="contenido" element={<Navigate to={ONBOARDING_V2.setup} replace />} />
                <Route path="producto" element={<Navigate to={ONBOARDING_V2.setup} replace />} />
                <Route path="piezas" element={<PrimerasPiezas />} />
                <Route path="listo" element={<MarcaLista />} />
              </Route>
            ) : (
              <Route path={ONBOARDING_V2.setup} element={<AccountSetupLayout />}>
                <Route index element={<AccountSetup />} />
                <Route path="categoria" element={<AccountSetupCategory />} />
                <Route path="contenido" element={<AccountSetupContent />} />
                <Route path="producto" element={<AccountSetupProduct />} />
              </Route>
            )}
            <Route path={ONBOARDING_V2.setupGenerando} element={<AccountSetupGenerating />} />
            {/* Resultados del onboarding: en desktop, dentro del shell (sidebar) con el
                layout clásico; en mobile, la pantalla previa (grilla 2×2) sin shell.
                Lo decide `AccountSetupResultsRoute` por viewport (ver arriba). */}
            <Route path={ONBOARDING_V2.setupResultados} element={<AccountSetupResultsRoute />} />
            {/* Invitación a un equipo: pública, el invitado puede no tener cuenta todavía.
                Spec 2026-09-11-invitaciones-equipo §3. */}
            <Route path="/unirme/:token" element={<UnirmeAEquipo />} />
            <Route path="/owner-onboarding" element={<OwnerOnboarding />} />
            {/* Alta + pago enterprise por URL personalizada (una por plan). Pública:
                la página maneja su propio signup/login. Ver ADR. */}
            <Route path="/alta-empresa/:plan" element={<EnterpriseCheckout />} />
            <Route path="/restablecer-clave" element={<ResetPassword />} />
            {/* Verificación de celular para cuentas nuevas (anti-abuso del trial). Pública
                con guard de sesión propio: acá manda el redirect bloqueante de
                checkUserProfile. Ver ADR-2026-08-31-verificacion-celular-supabase. */}
            <Route path="/verificar-celular" element={<VerificarCelular />} />
            <Route path="/ui-catalog" element={<UICatalog />} />
            <Route path="/ui-catalog-2" element={<UICatalog2 />} />

            {/* Pricing en modo preview — SÓLO dev (ADR-2026-07-29-ruta-preview-paywall-solo-desarrollo). Entra directo al
                catálogo rediseñado, sin sesión y sin pasar por el onboarding.
                En producción esta ruta no existe: da 404 como cualquier otra. */}
            {import.meta.env.DEV && (
              <Route path="/pricing-dev" element={<PricingDevPreview />} />
            )}

            {/* Todos los carteles de home a la vez — SÓLO dev. En la app son
                excluyentes (`useHomeBanners` resuelve uno solo, y cada uno depende de
                condiciones que tardan días en darse), así que compararlos entre sí
                obligaba a forzar la base un caso por vez. Monta el componente real
                (`HomeBannersView`) con estados simulados. En producción no existe. */}
            {import.meta.env.DEV && (
              <Route path="/banners-dev" element={<HomeBannersDevPreview />} />
            )}

            {/* Resultados del alta CON el upsell de planes — SÓLO dev (ADR-2026-07-31-pantalla-resultados-alta-upsell-hacer).
                Es la pantalla real (`AccountSetupResults`, con su shell en desktop y
                full-screen en mobile), no una maqueta: `devPreview` sólo saltea el
                rebote a `/bienvenida` sin sesión y arranca como si el usuario ya
                hubiera descargado, que es la condición del upsell (ADR-2026-07-29-upsell-planes-salir-alta-condicionado).
                El sheet se abre con "Seguir explorando Atlas", igual que en el flujo
                de verdad. En producción esta ruta no existe. */}
            {import.meta.env.DEV && (
              <Route
                path="/onboarding-pricing-dev"
                element={<AccountSetupResultsRoute devPreview />}
              />
            )}

            {/* ── Alias de las URLs con las que se probó el rediseño ──────────────
                Las doce pantallas nuevas se revisaron bajo estas URLs mientras el
                shell vivía detrás de `import.meta.env.DEV`. Al aplicarse el diseño
                sobre las rutas que YA existían (ADR-2026-07-31-shell-nuevo-urls-viejas), estas quedaron
                como atajo: redirigen a la canónica conservando query y hash.

                No montan la pantalla ellas mismas a propósito: con dos rutas vivas
                para lo mismo, la sidebar sólo puede marcar una de las dos.

                `/inicio` entró a esta lista con ADR-2026-07-31-inicio-se-funde-interfaz-v2:
                dejó de ser una pantalla propia. Su diseño ES ahora el de
                `/interfaz-v2`, que además tiene la lógica; mantener las dos habría
                dejado dos homes idénticas, una de ellas sin gating ni sheets. */}
            <Route path="/inicio" element={<AliasRuta destino={() => '/interfaz-v2'} />} />
            {/* Las tres URLs del sandbox de variantes de home
                (ADR-2026-08-31-home-tres-variantes-imagen-video) también quedan como
                alias: se compartieron para comparar las versiones, así que están en las
                pestañas y los favoritos de quien las revisó. Ahora las tres llevan a la
                home, que ES la versión 2 (ADR-2026-09-01-la-version-2-es-la-home).
                Sin esto quedaban en blanco: no hay ruta comodín. */}
            <Route path="/interfaz-v2-version1" element={<AliasRuta destino={() => '/interfaz-v2'} />} />
            <Route path="/interfaz-v2-version2" element={<AliasRuta destino={() => '/interfaz-v2'} />} />
            <Route path="/interfaz-v2-version3" element={<AliasRuta destino={() => '/interfaz-v2'} />} />
            {/* Las tres URLs del banco de pruebas del modo Modelo
                (ADR-2026-08-21-modelo-tres-variantes-de-diseno), por lo mismo: la
                variante v2 ganó y ES el modo, así que `/interfaz-v2/modo/modelo` la
                muestra y las otras dos pantallas se borraron
                (ADR-2026-09-02-modelo-v2-es-el-modo-modelo). Sin alias las tres URLs
                caían en la home muda: `/interfaz-v2/modo/:mode` no reconoce el slug y
                no abre ningún modo. */}
            <Route path="/interfaz-v2/modo/modelo-v1" element={<AliasRuta destino={() => '/interfaz-v2/modo/modelo'} />} />
            <Route path="/interfaz-v2/modo/modelo-v2" element={<AliasRuta destino={() => '/interfaz-v2/modo/modelo'} />} />
            <Route path="/interfaz-v2/modo/modelo-v3" element={<AliasRuta destino={() => '/interfaz-v2/modo/modelo'} />} />
            <Route path="/creaciones" element={<AliasRuta destino={() => '/redes-sociales/imagenes'} />} />
            <Route path="/editar-marca" element={<AliasRuta destino={() => '/mi-marca'} />} />
            <Route path="/generar" element={<AliasRuta destino={() => '/interfaz-v2'} />} />
            <Route path="/modo/:mode" element={<AliasRuta destino={(p) => `/interfaz-v2/modo/${p.mode}`} />} />
            <Route path="/plantillas" element={<AliasRuta destino={() => '/interfaz-v2/plantillas'} />} />
            <Route path="/coleccion/:slug" element={<AliasRuta destino={(p) => `/interfaz-v2/coleccion/${p.slug}`} />} />
            <Route path="/plantillas-belleza" element={<AliasRuta destino={() => '/interfaz-v2/plantillas-belleza'} />} />
            <Route path="/coleccion-belleza/:slug" element={<AliasRuta destino={(p) => `/interfaz-v2/coleccion-belleza/${p.slug}`} />} />
            <Route path="/estudio-video" element={<AliasRuta destino={() => '/editor-video'} />} />
            <Route path="/resultados" element={<AliasRuta destino={() => '/redes-sociales/imagenes-generadas'} />} />
            <Route path="/perfil" element={<AliasRuta destino={() => '/configuracion'} />} />

            {/* Design System v3 — pública, con layout propio (sidebar de carpetas
                anidadas). Sin guard ni DashboardLayout. Ver ADR-671. */}
            <Route path="/design-system" element={<DesignSystemLayout />}>
              <Route index element={<Navigate to={dsFirstPagePath()} replace />} />
              <Route path="getting-started" element={<DSGettingStartedPage />} />
              <Route path="foundations/typography" element={<DSTypographyPage />} />
              <Route path="foundations/colors" element={<DSColorsPage />} />
              <Route path="foundations/spacing" element={<DSSpacingPage />} />
              <Route path="foundations/shadows" element={<DSShadowsPage />} />
              <Route path="foundations/radius" element={<DSRadiusPage />} />
              <Route path="tokens/colors" element={<DSSemanticColorsPage />} />
              <Route path="components/icons" element={<DSIconsPage />} />
            </Route>
            <Route path="/" element={<HomeRedirect />} />

            {import.meta.env.DEV && (
              <Route path="/edicion-grafica-lab" element={<EdicionGraficaLab />} />
            )}
            {import.meta.env.DEV && (
              <Route path="/marca-de-agua-lab" element={<WatermarkLab />} />
            )}
            {/* Todo lo protegido — ProtectedRoute se monta UNA vez y persiste */}
            <Route element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
              {/* Protegidas SIN sidebar (pantalla completa) */}
              <Route path="/checkout" element={<Checkout />} />
              {/* Laboratorio del analizador de fotos — oculta, sin link; sólo dev testers. */}
              <Route path="/dev/analizador-fotos" element={<Suspense fallback={null}><AnalizadorFotosLab /></Suspense>} />
              {/* Portal de facturación de la suscripción (solo AR): pantalla propia
                  estilo portal de Stripe, sin el shell de la app. Ver
                  ADR-2026-08-03-facturacion-portal-stripe-completo. */}
              <Route path="/facturacion" element={<FacturacionPortal />} />
              {/* Oferta de facturación post-pago (solo AR): intermedia entre el
                  éxito del checkout y el estudio. Ver ADR-2026-08-13-facturacion-pantalla-post-pago-ar. */}
              <Route path="/facturacion-oferta" element={<FacturacionOferta />} />
              {/* Llamada de capacitación incluida del plan Ultra: intermedia entre
                  el éxito del pago y (si aplica) la oferta de facturación. Ver
                  ADR-2026-08-25-llamada-capacitacion-ultra-post-pago. */}
              <Route path="/capacitacion-ultra" element={<CapacitacionUltra />} />
              <Route path="/facturacion/factura/:id" element={<FacturaDetalle />} />
              <Route path="/redes-sociales/crear-anuncio" element={<Navigate to="/redes-sociales/crear-anuncio/seleccionar-productos" replace />} />
              {/* "Añadir nueva marca": con el onboarding de marca prendido, sus mismas pantallas en
                  tres pasos (ADR-2026-10-02-marca-nueva-como-el-onboarding). La ruta vieja redirige. */}
              {ONBOARDING_DE_MARCA && (
                <Route path={MARCA_NUEVA.link} element={<OnboardingMarcaLayout />}>
                  <Route index element={<LinkDeMarca />} />
                  <Route path="marca" element={<TableroDeMarca />} />
                  <Route path="lista" element={<MarcaLista />} />
                </Route>
              )}
              <Route
                path="/redes-sociales/descripcion-brand"
                element={<DescripcionDeMarcaONueva conOnboardingDeMarca={ONBOARDING_DE_MARCA} />}
              />
              <Route path="/onboarding/revisar-marca/:brandId" element={<RevisarMarca />} />

              {/* ── Rediseño: shell nuevo en desktop, el de siempre en mobile ─────
                  Las MISMAS URLs de antes; lo que cambió es el chrome que las
                  envuelve (ADR-2026-07-31-shell-nuevo-urls-viejas). `ShellApp` elige por viewport, así que
                  en mobile estas rutas siguen montando `DashboardLayout` igual que
                  las de abajo.

                  Van en su propio grupo y no dentro del de `DashboardLayout` porque
                  el layout es lo único que las distingue del resto. Al ser un padre
                  común, React mantiene el shell montado al navegar entre ellas y
                  sólo cambia el `<Outlet />`: la sidebar no se desmonta y el fade
                  entre secciones es posible (ADR-2026-07-31-ruta-layout-shell-sidebar-deja).

                  Todas conservan su URL: `/interfaz-v2` (la home) y `/interfaz-v2/*`
                  (los 8 modos, las plantillas y las colecciones), `/mi-marca`,
                  `/redes-sociales/imagenes`, `/redes-sociales/imagenes-generadas`,
                  `/editor-video` y `/configuracion`.

                  `/redes-sociales/imagenes-generadas` no está en la sidebar: es a
                  donde caen los ~9 handlers de fin de generación de `InterfazV2`, y
                  sin ella generar desde un modo expulsaba al shell viejo justo al
                  ver el resultado (ADR-2026-07-31-fail-closed-real-catalogo-enlaces).

                  `/editor-video/:projectId` —el editor de un proyecto— también está
                  acá desde ADR-2026-08-05-el-estudio-de-video-entra-al-shell. Estuvo
                  abajo, con `DashboardLayout`, porque es una superficie de pantalla
                  completa con su propio chrome; pero se entra a ella DESDE
                  `/editor-video`, que vive acá, así que abrir un proyecto cambiaba la
                  sidebar entera. Ahora usa el marco del shell como los otros dos
                  editores. */}
              <Route element={<ShellApp />}>
                {/* Acá abajo estuvieron `/interfaz-v2-version1|2|3`, el sandbox de
                    variantes de home (ADR-2026-08-31-home-tres-variantes-imagen-video).
                    La versión 2 ganó y ES esta ruta desde
                    ADR-2026-09-01-la-version-2-es-la-home; `estilos/home-sandbox/` se
                    borró con ella y las tres URLs quedaron como alias, arriba. */}
                <Route path="/interfaz-v2" element={<InterfazV2 />} />
                <Route path="/interfaz-v2/modo/:mode" element={<InterfazV2 />} />
                <Route path="/interfaz-v2/plantillas" element={<InterfazV2Plantillas />} />
                <Route path="/interfaz-v2/coleccion/:slug" element={<ColeccionDetalle />} />
                <Route path="/interfaz-v2/plantillas-belleza" element={<PlantillasBelleza />} />
                <Route path="/interfaz-v2/coleccion-belleza/:slug" element={<ColeccionDetalleBelleza />} />
                <Route path="/redes-sociales/imagenes" element={<GeneratedImagesHistory />} />
                <Route path="/redes-sociales/imagenes-generadas" element={<GeneratedImagesPage />} />
                <Route path="/mi-marca" element={<MiMarca />} />
                <Route path="/editor-video" element={<VideoProjects />} />
                <Route path="/editor-video/:projectId" element={<VideoEditor />} />
                {/* Flujos (canvas de nodos) — ADR-2026-09-04-canvas-flujos-enfoque. Piso
                    Pro/Ultra (ADR-2026-09-14-flujos-es-de-pro-y-ultra), dev_tester pasa
                    siempre: las páginas hacen su propio gate con `flujosVisible` (rebotan
                    a /subscription). `/flujo/:id` es el editor y se hospeda en neutro
                    (`rutasShellLegacy`) porque el canvas necesita el alto entero. */}
                <Route path="/flujos" element={<Suspense fallback={null}><FlujosPage /></Suspense>} />
                <Route path="/flujo/:id" element={<Suspense fallback={null}><FlujoEditor /></Suspense>} />
                <Route path="/configuracion" element={<Settings />} />
                {/* Pre-lanzamientos. Las pantallas se gatean solas por el flag de
                    empresa (`funciones_avanzadas.pre_lanzamientos`): la ruta existe
                    siempre para que un link compartido no dé 404, y adentro explica
                    cómo activarlo. */}
                <Route path="/lanzamientos" element={<Lanzamientos />} />
                <Route path="/lanzamientos/:id" element={<LanzamientoDetalle />} />
                {/* Instagram tiene su sección propia, con el mismo flag
                    (ADR-2026-10-06-instagram-tiene-su-propia-seccion). */}
                <Route path="/instagram" element={<CalendarioDeInstagram />} />
                {/* El detalle de las métricas de cada tienda. El resumen vive en
                    `/lanzamientos`, junto a la lista
                    (ADR-2026-10-06-metricas-y-lanzamientos-en-una-pagina). La ruta existe
                    siempre (`metricas` es estático y le gana a `:id`); la página manda a
                    Tienda online a quien no es dev_tester
                    (ADR-2026-10-07-metricas-solo-para-dev-testers). */}
                <Route path="/lanzamientos/metricas/:plataforma" element={<MetricasDePlataformaPage />} />
                {/* `/ayuda` se mudó acá desde el grupo de `DashboardLayout`
                    (ADR-2026-08-04-ayuda-entra-al-shell-nuevo): es una sección más —la
                    enlazan el pie de la sidebar y el menú de cuenta— y era la única que
                    seguía abriendo con el chrome viejo y con el panel blanco. */}
                <Route path="/ayuda" element={<Ayuda />} />
                {/* Programa de referidos (ADR-2026-08-27-programa-referidos).
                    Sección propia y no una pestaña de Configuración: es una
                    superficie de crecimiento —tiene que poder linkearse y
                    encontrarse— y enterrada en ajustes no la abre nadie. */}
                <Route path="/referidos" element={<Referidos />} />
                {/* Centro de notificaciones (ADR-2026-08-31-centro-de-notificaciones).
                    Sección del shell y no un modal: en mobile ESTA es la lista —la
                    campana del header flotante lleva derecho acá, sin popover— y en
                    desktop es el historial completo detrás del "Ver todas" del panel. */}
                <Route path="/notificaciones" element={<Notificaciones />} />
                {/* `/subscription` también se mudó acá
                    (ADR-2026-08-04-subscription-entra-al-shell-nuevo). Es a donde lleva
                    "Suscripción" del menú de cuenta, y montada en `DashboardLayout` la
                    navegación desmontaba el shell nuevo, pintaba el chrome viejo y lo
                    dejaba a la vista el segundo largo que tarda la pantalla en cargar
                    sus datos, hasta que el catálogo portaleado tapaba todo. */}
                <Route path="/subscription" element={<Paywall />} />
                {/* ── Las que se alcanzan DESDE el shell nuevo ─────────────────
                    ADR-2026-08-05-las-pantallas-que-el-shell-enlaza-viven-en-el-shell.

                    Todas éstas se abren desde una pantalla que ya vive acá —el visor de
                    creaciones, la grilla, Configuración, la home— y estaban montadas
                    abajo, así que al entrar cambiaba la sidebar entera. El barrido salió
                    de cruzar las rutas del grupo viejo contra lo que enlazan las
                    pantallas del nuevo; lo que quedó afuera es lo que nadie enlaza desde
                    acá (ver el comentario del grupo de `DashboardLayout`).

                    Ninguna necesitó cambios propios: las que anclan un panel `fixed` lo
                    hacen contra `sidebarWidth`, que este shell re-provee con su
                    geometría, y el resto es contenido en flujo dentro del `<Outlet />`. */}
                {/* "Edición Canva", desde el menú IA del visor. */}
                <Route
                  path="/redes-sociales/edicion-grafica"
                  element={<EdicionGrafica />}
                />
                {/* "Editar" del visor y de las ediciones rápidas de la home. */}
                <Route path="/redes-sociales/editar-imagen" element={<EditarImagen />} />
                {/* "Exportar a Tiendanube", botón de la grilla de Creaciones. */}
                <Route path="/exportar-tiendanube" element={<ExportarTiendanube />} />
                {/* Los dos editores de video que enlaza Creaciones. */}
                <Route path="/redes-sociales/editar-video" element={<EditarVideo />} />
                <Route
                  path="/redes-sociales/editar-video-ia"
                  element={<EditarVideoIAHistorial />}
                />
                {/* "Facturación", del menú de Configuración. */}
                <Route path="/billing" element={<Billing />} />
                {/* El catálogo de modos, que enlazan la home y el shell. */}
                <Route path="/modos" element={<Modos />} />
                {/* "Mostrar cómo funciona" vive ACÁ y no con Shorts Studio / Animar /
                    Carrusel: nació con el shell nuevo y toma la anatomía de Automático
                    (ADR-2026-09-03-modo-mostrar-como-funciona-en-el-shell-nuevo). Montado en
                    `DashboardLayout` abría con la sidebar vieja. */}
                <Route path="/mostrar-como-funciona" element={<MostrarComoFuncionaMode />} />
                <Route path="/variantes-de-acabado" element={<VariantesAcabadoMode />} />
                {/* "Ambiente completo" — mismo grupo y por lo mismo que el de arriba:
                    toma la anatomía de Automático y necesita el shell nuevo
                    (ADR-2026-09-03-modo-ambiente-completo). */}
                <Route path="/ambiente-completo" element={<AmbienteCompletoMode />} />
                {/* Academia Atlas — ADR-2026-08-24-atlas-academy-seccion-nueva. Va en
                    este grupo y no en el de `DashboardLayout` porque se entra DESDE la
                    home, que vive acá: montada abajo, ir a la academia desmontaba el
                    shell nuevo y pintaba el chrome viejo (el mismo motivo por el que
                    se mudaron `/ayuda` y `/subscription`).

                    Tres rutas: el listado, la lección y el examen. La lección va en la
                    URL (ADR-2026-08-24-la-leccion-vive-en-la-url), así que el atrás del
                    navegador recorre el curso y una lección puntual se puede compartir.

                    `/examen` va ANTES que `/:leccionId` por claridad, aunque no haga
                    falta: React Router rankea los segmentos estáticos por encima de los
                    dinámicos, así que una lección con id "examen" tampoco lo pisaría. */}
                <Route path="/academia" element={<AtlasAcademy />} />
                <Route path="/academia/:slug" element={<CursoDetalle />} />
                <Route
                  path="/academia/:slug/examen"
                  element={
                    <SoloConExamen>
                      <PantallaExamen />
                    </SoloConExamen>
                  }
                />
                {/* Las respuestas del último intento, fuera del momento de rendirlo
                    (ADR-2026-08-24-la-revision-sobrevive-al-intento). Estática como
                    `examen`, así que tampoco la pisa `:leccionId`. */}
                <Route
                  path="/academia/:slug/revision"
                  element={
                    <SoloConExamen>
                      <PantallaRevision />
                    </SoloConExamen>
                  }
                />
                <Route path="/academia/:slug/:leccionId" element={<CursoDetalle />} />

                {/* ── Mudadas desde `DashboardLayout` por el desktop ────────────
                    ADR-2026-08-20-la-sidebar-vieja-la-elegia-el-grupo-de-rutas.

                    Estas montaban el `<aside>` VIEJO en desktop, no por su
                    ancho sino por estar declaradas en el otro grupo. Tres de
                    ellas —Shorts Studio, Animar imagen y Carrusel— las enlaza
                    "Todas las herramientas" de la sidebar NUEVA, así que
                    clickear un ítem de la sidebar nueva cambiaba la sidebar
                    entera.

                    Van en ESTE grupo y no en uno nuevo a propósito: dos
                    `<Route element>` distintos que montan `EstiloMinimal` son
                    dos posiciones distintas del árbol, y React lo remontaría al
                    cruzar de uno al otro (sidebar parpadeando, fade perdido).

                    En mobile no cambia nada: `ShellApp` sigue eligiendo por
                    viewport y `ShellMobile` les deja la barra inferior vieja
                    (ver `esRutaShellLegacy` allá arriba). Y en desktop se
                    hospedan en NEUTRO —sin la columna del 90%— porque se
                    escribieron contra `.main-content`; eso lo resuelve
                    `EstiloMinimal` con la misma lista. */}
                {/* Post-checkout. */}
                <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                {/* Chat con el agente de Atlas. Es la MISMA conversación que el
                    panel lateral (los dos leen `ChatAtlasProvider`), no otra.
                    La abre el botón de expandir de `PanelChatAtlas`. */}
                <Route path={RUTA_CHAT_ATLAS} element={<PantallaChatAtlas />} />
                {/* El Chat se llama Agente desde
                    ADR-2026-09-12-el-agente-toma-el-diseno-del-asistente-v2; la URL
                    vieja sigue abriendo. */}
                <Route path="/chat" element={<AliasRuta destino={() => RUTA_CHAT_ATLAS} />} />
                {/* Shorts Studio, Animar imagen y Carrusel son los modos con
                    pantalla propia (`Modos.tsx` intercepta su path y navega
                    acá). Son las que enlaza la sidebar nueva. */}
                <Route path="/shorts-studio" element={<ShortsStudioMode />} />
                <Route path="/animar-imagen" element={<AnimarImagenMode />} />
                <Route path="/carrusel" element={<CarruselMode />} />
                {/* Mismo grupo y por lo mismo: pantalla propia con el shell nuevo
                    (`ShellModoRedisenado`), gateada por rubro adentro del componente —
                    quien llega por URL sin ser de Belleza y Cuidado Personal rebota a la
                    home (ADR-2026-09-10-modo-mostrar-como-funciona-belleza). */}
                <Route
                  path="/mostrar-como-funciona-belleza"
                  element={<MostrarComoFuncionaBellezaMode />}
                />
                {/* El otro modo del rubro, mismo trato: pantalla propia y gate adentro
                    del componente (ADR-2026-09-10-modo-replicar-foto-belleza). */}
                <Route path="/replicar-foto" element={<ReplicarFotoMode />} />
                {/* Diseño de indumentaria: la portada y el lienzo de un diseño. Se hospeda
                    en neutro (`RUTAS_SHELL_LEGACY`) porque es una pantalla de trabajo a
                    ancho completo, como el Agente. */}
                <Route path="/diseno-de-indumentaria" element={<DisenoIndumentariaMode />} />
                <Route path="/diseno-de-indumentaria/:disenoId" element={<DisenoIndumentariaMode />} />
                {/* Redes: la portada y el lienzo de una sesión (ADR-2026-10-01-modo-redes).
                    Mismo trato que Diseño: pantalla de trabajo a ancho completo. */}
                <Route path="/redes" element={<RedesMode />} />
                <Route path="/redes/:sesionId" element={<RedesMode />} />
                {/* Pantalla intermedia de los atajos de "Ediciones rápidas" —
                    ADR-2026-08-20-los-atajos-tienen-pantalla-propia-para-elegir-imagen.
                    Un slug desconocido se auto-redirige a la home desde el componente,
                    así que no hace falta un catch-all acá. */}
                <Route path="/atajo/:accion" element={<AtajoImagenMode />} />
                {/* Las otras DOS que enlaza la sidebar nueva —
                    ADR-2026-09-10-lo-que-enlaza-la-sidebar-nueva-vive-en-el-shell-nuevo.

                    "Manager de Redes IA" y "Test V1" son ítems de `ACCESOS_INTERNOS`
                    (`EstiloMinimal`), la lista devOnly que nació DESPUÉS del barrido de
                    ADR-2026-08-05 y por eso nunca se cruzó contra este grupo: seguían
                    declaradas abajo, así que clickear un ítem de la sidebar nueva
                    devolvía la sidebar vieja. Es el mismo caso que Shorts, Animar y
                    Carrusel, con la misma solución. */}
                <Route path="/redes-sociales/calendario" element={<CalendarRouter />} />
                <Route path="/test-v1" element={<TestV1 />} />
                {/* Estudio de marketing — Bloque A. Ruta de layout para que el contexto
                    compartido no se remonte al cambiar de pantalla. */}
                <Route path="/estudio-marketing" element={<EstudioMarketingLayout />}>
                  <Route index element={<Navigate to="/estudio-marketing/mapa" replace />} />
                  <Route path="mapa" element={<MapaPage />} />
                  <Route path="plan" element={<PlanPage />} />
                  <Route path="plan/:piezaId" element={<PiezaPage />} />
                  {/* "Análisis de datos": ruta de layout SIN path propio. Las
                      dos pantallas fusionadas conservan sus URLs de siempre —
                      son las dos pestañas — así que los cinco `destino` del
                      mapa y los links ya compartidos siguen andando
                      (ADR-2026-08-17-analisis-datos-tabs-por-ruta). */}
                  <Route element={<AnalisisDatosPage />}>
                    <Route path="insights" element={<InsightsPage />} />
                    <Route path="estrategia" element={<EstrategiaPage />} />
                  </Route>
                  <Route path="marca" element={<MarcaPage />} />
                  {/* Configuración de datos: las conexiones reales de las tres
                      fuentes + la base de conocimiento. Es el destino de los
                      nodos de fuente del mapa
                      (ADR-2026-08-17-estudio-marketing-nodos-fuente-a-datos). */}
                  <Route path="datos" element={<ConfiguracionDatosPage />} />
                  {/* Puesta en marcha: el flujo de configuración inicial. Va
                      DENTRO del layout —con la sidebar a la vista— a propósito:
                      es una pantalla más de la sección, no un wizard a pantalla
                      completa (ADR-2026-08-17-estudio-marketing-puesta-en-marcha).
                      Es un contenedor de pasos: el paso vive en estado, no en la
                      URL, así que sumar el paso 2 no suma una ruta. */}
                  <Route path="onboarding" element={<PuestaEnMarchaPage />} />
                </Route>
              </Route>

              {/* Shell con sidebar VIEJA — `DashboardLayout` persiste entre todas
                  estas. Lo que queda acá después de
                  ADR-2026-08-20-la-sidebar-vieja-la-elegia-el-grupo-de-rutas:

                   · el flujo clásico de anuncios (17 pantallas), que quedó fuera
                     del alcance de esa pasada. Es la home del Grupo A —y de las
                     cuentas sin grupo— así que NO se puede mudar sin decidir antes
                     qué pasa con ese flujo;
                   · 18 rutas del ERP viejo (registro de ventas, stock, envíos,
                     forecasting…) que NO las enlaza nadie: ni un ítem de menú, ni
                     un `navigate`, ni un `Link`. Ocho son `PlaceholderPage`
                     vacías. Se llega sólo escribiendo la URL a mano, así que la
                     sidebar vieja que montan no la ve nadie y se dejan quietas.

                  Este grupo es lo único que impide borrar `components/Sidebar.tsx`
                  entero; su chrome de mobile (`md:hidden`) lo sigue usando toda la
                  app igual. */}
              <Route element={<DashboardLayout><Outlet /></DashboardLayout>}>
                <Route path="/registro-ventas" element={<SalesRegister />} />
                <Route path="/historial-ventas" element={<PlaceholderPage title="Historial de ventas" />} />
                <Route path="/envios" element={<PlaceholderPage title="Envíos" />} />
                <Route path="/productos" element={<PriceList />} />
                <Route path="/stock" element={<Stock />} />
                <Route path="/descuentos" element={<Discounts />} />
                <Route path="/registro-compras" element={<PlaceholderPage title="Registro de compras" />} />
                <Route path="/historial-compras" element={<PlaceholderPage title="Historial de compras" />} />
                <Route path="/forecasting" element={<PlaceholderPage title="Forecasting" />} />
                <Route path="/proveedores" element={<PlaceholderPage title="Proveedores" />} />
                <Route path="/flujo-caja" element={<PlaceholderPage title="Flujo de caja" />} />
                <Route path="/forecasting-financiero" element={<PlaceholderPage title="Forecasting financiero" />} />
                <Route path="/gastos-recurrentes" element={<PlaceholderPage title="Gastos recurrentes" />} />
                <Route path="/mensajes" element={<Messages />} />
                <Route path="/graficos-de-interaccion" element={<InteractionCharts />} />
                <Route path="/clientes" element={<PlaceholderPage title="Clientes" />} />
                <Route path="/subir-datos" element={<DataUpload />} />
                <Route path="/sincronizacion-automatica" element={<AutomaticSync />} />
                <Route path="/redes-sociales/crear-anuncio/seleccionar-productos" element={<SeleccionarProductosAnuncio />} />
                <Route path="/redes-sociales/crear-anuncio/disenar-video" element={<DisenarVideo />} />
                <Route path="/redes-sociales/crear-anuncio/configurar-video-influencer-nuevo" element={<ConfigurarVideoInfluencerNuevo />} />
                <Route path="/redes-sociales/crear-anuncio/configurar-video-influencer-detalle" element={<ConfigurarVideoInfluencerDetalle />} />
                <Route path="/redes-sociales/crear-anuncio/configurar-video-influencer-video" element={<ConfigurarVideoInfluencerVideo />} />
                <Route path="/redes-sociales/crear-anuncio/configurar-video-influencer" element={<ConfigurarVideoInfluencer />} />
                <Route path="/redes-sociales/crear-anuncio/configurar-video-personalizado" element={<ConfigurarVideoPersonalizado />} />
                <Route path="/redes-sociales/crear-anuncio/configurar-video-cinematografico" element={<ConfigurarVideoCinematografico />} />
                <Route path="/redes-sociales/crear-anuncio/planificador-video-cinematografico" element={<PlanificadorVideoCinematografico />} />
                <Route path="/redes-sociales/video-generado" element={<GeneratedVideoPage />} />
                <Route path="/redes-sociales/crear-anuncio/seleccionar-pose" element={<SeleccionarPoseModelo />} />
                <Route path="/redes-sociales/marketing-plan-chat" element={<MarketingPlanChat />} />
                <Route path="/redes-sociales/plan-marketing-view" element={<MarketingPlanReview />} />

                {/* Antes gateadas por BetaTesterRoute (removido: `beta_tester`
                    quedó en desuso con el sistema de grupos y era `true` para
                    todos). Ahora son rutas autenticadas normales; la visibilidad
                    se controla por el nav según el grupo. */}
                <Route path="/redes-sociales/crear-anuncio/prueba-nueva-interfaz" element={<PruebaNuevaInterfaz />} />
                <Route path="/redes-sociales/crear-anuncio/seleccionar-texto-estudio-virtual" element={<SeleccionarTextoEstudioVirtual />} />
                <Route path="/redes-sociales/crear-anuncio/anadir-a-la-escena" element={<AnadirAEscena />} />
                <Route path="/redes-sociales/crear-anuncio/personalizar-modelo-estudio-virtual" element={<PersonalizarModeloEstudioVirtual />} />
              </Route>
            </Route>
          </Routes>
          </ChatAtlasRaiz>
          </SidebarProvider>
        </PermissionProvider>
      </Router>
    </MobileBlocker>
  );
}

export default App;